import { useState, useEffect, useRef, useCallback } from "react";

// ═══════════════════════════════════
// KUBEWORLD V6 — FINAL BUILD
// ═══════════════════════════════════

import { CH, CW, T, VH, VW, getLv, getNxt, isWalk, pickRandom } from './jeu/base.js';
import { BOSS_ARENAS, ENTREES, INTERIOR_MAP, INTERIOR_MAPS, INTERIOR_MAPS_B, MAPS, PORTALS, SURFACES, ZONE_INTROS, ZONE_REQ } from './jeu/contenu/monde.js';
import { HOUSES, NPCS } from './jeu/contenu/personnages.js';
import { BOSSES, BOSS_NAME_MAP, CHEST_LOOT, MISSIONS, ZONE_ENEMIES } from './jeu/contenu/scenarios.js';
import { clock, dSprite, dTile, getNight } from './jeu/rendu.js';
import { dessinerHalos } from './jeu/tuiles.js';
import { S } from './jeu/styles.js';
import { chargerAtlas, atlasPret } from './jeu/atlas.js';

// ═══ MAIN ═══
export default function KubeWorldV6(){
  const cvs=useRef(null);
  const [zone,setZone]=useState('hub');
  const [pos,setPos]=useState({x:13,y:9});
  const [dir,setDir]=useState('down');
  const [walk,setWalk]=useState(false);
  const [xp,setXp]=useState(0);
  const [hearts,setHearts]=useState(3);
  const [questsDone,setQD]=useState({});
  const [seenQ,setSeenQ]=useState({}); // questions deja vues par PNJ : {nom:[index,...]}
  const [bossWins,setBW]=useState({});
  const [inv,setInv]=useState(['kubectl get pods']);
  const [enemies,setEnemies]=useState([]);
  const [chests,setChests]=useState([]);
  const [chestsOpened,setCO]=useState({});
  const [events,setEvents]=useState([]);
  const [gt,setGt]=useState(480);
  const [journal,setJnl]=useState([]);
  // In-house state
  const [inHouse,setInHouse]=useState(null); // {house, pos, dir}
  // UI overlays
  const [dialogUI,setDlg]=useState(null);
  const [questUI,setQst]=useState(null);
  const [bossUI,setBoss]=useState(null);
  const [misUI,setMis]=useState(null); // mission terminal en cours
  const [termUI,setTerm]=useState(null);
  const [overlay,setOv]=useState(null);
  const [recap,setRecap]=useState(null);
  const [notif,setNotif]=useState('');
  const [portalMsg,setPMsg]=useState('');
  const [loaded,setLoaded]=useState(false);
  const [tuiles,setTuiles]=useState(false); // planches de tuiles chargees
  // Terminal simulator
  const [termOpen,setTermOpen]=useState(null); // 'split','full','float'
  const [termHist,setTermHist]=useState([{type:'sys',text:'KubeWorld Terminal v1.0 — Tape "help" pour commencer'}]);
  const [termCmd,setTermCmd]=useState('');
  const [termCmdHist,setTermCmdHist]=useState([]);
  const [termHistIdx,setTermHistIdx]=useState(-1);
  // World map, flashcards, daily
  const [showMap,setShowMap]=useState(false);
  const [showFlash,setShowFlash]=useState(false);
  const [flashCard,setFlashCard]=useState(null);
  // Achievements
  const [achievements,setAch]=useState({});
  const [enemyCount,setEnemyCount]=useState(0);
  const [termCount,setTermCount]=useState(0);
  const [flashCount,setFlashCount]=useState(0);
  const [showVictory,setShowVictory]=useState(false);
  // Boss arena state
  const [bossArena,setBossArena]=useState(null); // {id, phase, tick}
  // Zone transition effect
  const [zoneTransition,setZoneTransition]=useState(null); // {zone, alpha, text}
  // Screen shake
  const shakeRef=useRef(0);
  // Sound engine
  const audioCtxRef=useRef(null);
  // Story intro
  const [showIntro,setShowIntro]=useState(()=>!localStorage.getItem('kw6_intro_done'));
  const [introIdx,setIntroIdx]=useState(0);
  const [zonesVisited,setZonesVisited]=useState(()=>{try{return JSON.parse(localStorage.getItem('kw6_visited'))||{hub:true};}catch{return{hub:true};}});
  const [zoneIntroShow,setZoneIntroShow]=useState(null);
  const [bossIntro,setBossIntro]=useState(null); // {boss, idx}
  const todayKey=new Date().toDateString();
  const [dailyDone,setDailyDone]=useState(()=>localStorage.getItem('kw6_daily')===todayKey);
  const [dailyChallenge]=useState(()=>{const saved=localStorage.getItem('kw6_daily_challenge');if(saved)try{const parsed=JSON.parse(saved);if(parsed._date===todayKey)return parsed;} catch{}const c={...pickRandom(Object.values(ZONE_ENEMIES).flat()),_date:todayKey};localStorage.setItem('kw6_daily_challenge',JSON.stringify(c));return c;});

  const tickR=useRef(0),keysR=useRef({}),cdR=useRef(0),compP=useRef({x:12,y:9}),iRef=useRef(null),mRef=useRef(null),posRef=useRef({x:12,y:9});

  // Les planches de tuiles doivent etre la avant le premier dessin, sinon la premiere
  // image est vide et le joueur voit un ecran noir au chargement.
  useEffect(()=>{chargerAtlas().then(()=>setTuiles(true)).catch(e=>{console.error('KubeWorld: planches de tuiles',e);setTuiles(true);});},[]);

  // ⭐ Les dix premieres secondes : au tout premier lancement, quelqu'un vient te parler.
  // Sans ca on arrive au milieu d'une salle grise, entoure d'inconnus muets, et rien
  // n'indique qu'il faut appuyer sur E (regle R10).
  const accueil=useRef(false);
  useEffect(()=>{
    if(!loaded||!tuiles||showIntro||accueil.current)return;
    if(zone!=='hub'||xp>0||Object.keys(questsDone).length)return;
    // ⚠️ Une seule fois par joueur, pas a chaque rechargement : sinon on se fait
    // accueillir en boucle, et toute reprise de partie neuve rouvre le dialogue.
    try{if(localStorage.getItem('kw6_accueil'))return;localStorage.setItem('kw6_accueil','1');}catch{}
    accueil.current=true;
    const hote=(NPCS.hub||[]).find(n=>n.name==='Admin Kube');
    if(hote){setDir('up');setTimeout(()=>setDlg({npc:hote,idx:0,accueil:true}),1300);}
  },[loaded,tuiles,showIntro,zone,xp,questsDone]);

  // Save/Load
  useEffect(()=>{(async()=>{try{const r=localStorage.getItem('kw6');if(r){const d=JSON.parse(r);const z0=(d.z&&MAPS[d.z])?d.z:'hub';setZone(z0);const p0=d.p&&isWalk(MAPS[z0][d.p.y]?.[d.p.x])?d.p:ENTREES[z0];setPos(p0);posRef.current=p0;compP.current={...p0};if(d.p&&(p0.x!==d.p.x||p0.y!==d.p.y))setTimeout(()=>nfy('🚧 Zone remodelée : tu as été replacé à l\'entrée'),800);if(d.xp)setXp(d.xp);if(d.h)setHearts(d.h);if(d.qd)setQD(d.qd);if(d.sq)setSeenQ(d.sq);if(d.bw)setBW(d.bw);if(d.inv)setInv(d.inv);if(d.jnl)setJnl(d.jnl);if(d.co)setCO(d.co);if(d.gt)setGt(d.gt);if(d.ach)setAch(d.ach);if(d.ec)setEnemyCount(d.ec);if(d.tc)setTermCount(d.tc);if(d.fc)setFlashCount(d.fc);}}catch(e){console.warn('KubeWorld: failed to load save',e);}setLoaded(true);})();},[]);
  useEffect(()=>{if(!loaded)return;(async()=>{try{localStorage.setItem('kw6',JSON.stringify({z:zone,p:pos,xp,h:hearts,qd:questsDone,sq:seenQ,bw:bossWins,inv,jnl:journal,co:chestsOpened,gt,ach:achievements,ec:enemyCount,tc:termCount,fc:flashCount}));}catch(e){console.warn('KubeWorld: failed to save',e);}})();},[loaded,zone,pos,xp,hearts,questsDone,seenQ,bossWins,inv,journal,chestsOpened,achievements,enemyCount,termCount,flashCount]);

  const nfy=msg=>{setNotif(msg);setTimeout(()=>setNotif(''),3000);};
  const addXP=a=>{setXp(x=>x+a);nfy(`+${a} XP`);};

  const ACHS={
    first_quest:{icon:'🌟',name:'Premier Pas',desc:'Terminer la première quête'},
    bug_hunter:{icon:'🐛',name:'Tueur de Bugs',desc:'Vaincre 10 ennemis'},
    kubectl_master:{icon:'⌨️',name:'Maître kubectl',desc:'Débloquer 12+ commandes'},
    boss_slayer:{icon:'💀',name:'Boss Slayer',desc:'Vaincre les 4 boss'},
    terminal_warrior:{icon:'💻',name:'Terminal Warrior',desc:'Résoudre 20 incidents'},
    flash_addict:{icon:'🃏',name:'Flashcard Addict',desc:'Consulter 30 flashcards'},
    completionist:{icon:'👑',name:'Completionist',desc:'Terminer toutes les quêtes de toutes les zones'},
  };
  const checkAch=useCallback((key)=>{
    setAch(prev=>{if(prev[key])return prev;nfy(`🏆 Achievement: ${ACHS[key]?.name}`);return{...prev,[key]:true};});
  },[]);

  // ═══ SOUND ENGINE ═══
  const playSound=useCallback((type)=>{
    try{
      if(!audioCtxRef.current)audioCtxRef.current=new(window.AudioContext||window.webkitAudioContext)();
      const ctx=audioCtxRef.current;const now=ctx.currentTime;
      const osc=ctx.createOscillator();const gain=ctx.createGain();
      osc.connect(gain);gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.08,now);
      if(type==='quest'){osc.frequency.setValueAtTime(523,now);osc.frequency.setValueAtTime(659,now+0.1);osc.frequency.setValueAtTime(784,now+0.2);gain.gain.exponentialRampToValueAtTime(0.001,now+0.4);osc.start(now);osc.stop(now+0.4);}
      else if(type==='wrong'){osc.frequency.setValueAtTime(300,now);osc.frequency.setValueAtTime(200,now+0.15);gain.gain.exponentialRampToValueAtTime(0.001,now+0.3);osc.start(now);osc.stop(now+0.3);}
      else if(type==='boss'){osc.frequency.setValueAtTime(392,now);osc.frequency.setValueAtTime(523,now+0.15);osc.frequency.setValueAtTime(659,now+0.3);osc.frequency.setValueAtTime(784,now+0.45);gain.gain.exponentialRampToValueAtTime(0.001,now+0.7);osc.start(now);osc.stop(now+0.7);}
      else if(type==='portal'){osc.type='sine';osc.frequency.setValueAtTime(200,now);osc.frequency.exponentialRampToValueAtTime(800,now+0.3);gain.gain.exponentialRampToValueAtTime(0.001,now+0.4);osc.start(now);osc.stop(now+0.4);}
      else if(type==='hit'){osc.type='sawtooth';osc.frequency.setValueAtTime(150,now);gain.gain.exponentialRampToValueAtTime(0.001,now+0.15);osc.start(now);osc.stop(now+0.15);}
      else if(type==='chest'){osc.frequency.setValueAtTime(440,now);osc.frequency.setValueAtTime(554,now+0.1);osc.frequency.setValueAtTime(659,now+0.2);gain.gain.exponentialRampToValueAtTime(0.001,now+0.35);osc.start(now);osc.stop(now+0.35);}
    }catch{}
  },[]);
  const triggerShake=(duration=12)=>{shakeRef.current=duration;};

  // ═══ TERMINAL ENGINE ═══
  const termRef2=useRef(null);
  const execCmd=useCallback((raw)=>{
    const cmd=raw.trim();if(!cmd)return;
    setTermCmdHist(h=>[cmd,...h].slice(0,50));setTermHistIdx(-1);
    const add=(type,text)=>setTermHist(h=>[...h,{type,text}].slice(-100));
    add('cmd',`$ ${cmd}`);
    const parts=cmd.toLowerCase().split(/\s+/);

    // ═══ MISSION EN COURS ═══
    // Toute commande tapee est confrontee a l'etape courante. Une bonne reponse affiche
    // la sortie de la mission et s'arrete la ; une mauvaise laisse le terminal repondre
    // normalement, pour que le joueur voie ce que sa commande fait vraiment.
    if(misUI&&!misUI.result&&['kubectl','etcdutl','etcdctl'].includes(parts[0])){
      const ph=misUI.m.phases[misUI.phase];
      if(ph.a.some(a=>cmd.toLowerCase().includes(a.toLowerCase()))){
        playSound('quest');add('out',ph.ok);
        setMis(u=>u?{...u,result:'ok'}:u);return;
      }
      const nf=misUI.fails+1;
      if(nf>=3){
        add('sys',`\ud83d\udca1 La commande attendue etait : ${ph.a[0]}`);add('out',ph.ok);
        setMis(u=>u?{...u,fails:nf,result:'ok'}:u);return;
      }
      setMis(u=>u?{...u,fails:nf}:u);
      add('sys',`\ud83d\udca1 ${ph.h[Math.min(nf-1,ph.h.length-1)]}`);
    }
    const npcs=NPCS[zone]||[];
    const ZONE_NAMES={hub:'Cluster Hub',pods:'Pod Village',workloads:'Workload Fortress',network:'Network Realm',sanctum:'Arcane Sanctum'};
    const zoneData={name:ZONE_NAMES[zone]||zone};

    // help
    if(parts[0]==='help'){add('out',
`Commandes disponibles :
  kubectl get pods [-n namespace] [-A] [-l label]
  kubectl get nodes
  kubectl get svc / services
  kubectl get deploy / deployments
  kubectl get ns / namespaces
  kubectl get events
  kubectl get endpoints [name]
  kubectl describe pod <name>
  kubectl logs <name> [--previous]
  kubectl top pod
  kubectl auth can-i <verb> <resource>
  kubectl debug -it <pod> --image=busybox
  kubectl get pvc / pv / storageclass
  clear — Effacer le terminal
  whoami — Ton profil
  map — Carte des zones
  daily — Challenge du jour
  daily done — Valider le challenge du jour (+50 XP)

💡 Les commandes se débloquent en parlant aux PNJ !`);return;}

    if(parts[0]==='clear'){setTermHist([{type:'sys',text:'Terminal effacé.'}]);return;}

    if(parts[0]==='whoami'){const lv=getLv(xp);add('out',`${lv.t} (Level ${lv.l})\nXP: ${xp} | ❤️: ${hearts}/3\nZone: ${zone}\nCommandes apprises: ${inv.length}\nQuêtes: ${Object.keys(questsDone).length}\nBoss vaincus: ${Object.keys(bossWins).length}/${Object.keys(BOSSES).length}`);return;}

    if(parts[0]==='map'){add('out',
`CLUSTER MAP:
  🏠 Hub .............. (accessible)
  🏘️ Pod Village ...... ${bossWins.crash_lord?'✅ Boss vaincu':'Level 1'}
  🏰 Workload Fortress  ${bossWins.rollout_reaper?'✅ Boss vaincu':bossWins.crash_lord?'🔓 Débloqué':'🔒 Level 3 + Boss Pods'}
  🌐 Network Realm .... ${bossWins.phantom_404?'✅ Boss vaincu':bossWins.rollout_reaper?'🔓 Débloqué':'🔒 Level 5 + Boss Workloads'}
  🔮 Arcane Sanctum ... ${bossWins.etcd_corruptor?'✅ Boss vaincu':bossWins.phantom_404?'🔓 Débloqué':'🔒 Level 7 + Boss Network'}
Position: (${pos.x}, ${pos.y})`);return;}

    if(parts[0]==='daily'){
      if(dailyDone){add('out','✅ Challenge du jour déjà terminé ! Reviens demain.');return;}
      if(parts[1]==='done'){addXP(50);setDailyDone(true);localStorage.setItem('kw6_daily',todayKey);add('out','🏆 Challenge du jour validé ! +50 XP');return;}
      add('out',`🎯 DAILY CHALLENGE:\n${dailyChallenge.icon} ${dailyChallenge.name}\n${dailyChallenge.d}\n\nAnalyse le problème et tape "daily done" pour valider (+50 XP) !`);return;
    }

    if(parts[0]!=='kubectl'&&parts[0]!=='etcdutl'&&parts[0]!=='etcdctl'){add('err',`bash: ${parts[0]}: command not found`);return;}

    // Check if command is learned
    const cmdBase=parts.slice(0,3).join(' ');
    const hasCmd=inv.some(c=>cmd.toLowerCase().includes(c.toLowerCase().split(' ').slice(0,2).join(' ')));
    if(!hasCmd&&parts[0]==='kubectl'&&parts[1]!=='get'){add('err',`⚠️ Tu n'as pas encore appris cette commande !\nParle aux PNJ pour débloquer de nouvelles commandes.\nCommandes connues : ${inv.join(', ')}`);return;}

    // kubectl port-forward : le geste qui isole le Service de l'application
    if(parts[0]==='kubectl'&&(parts[1]==='port-forward'||parts[1]==='pf')){
      const cible=parts[2]||'pod/<nom>';
      add('out',`Forwarding from 127.0.0.1:8080 -> 8080\nForwarding from [::1]:8080 -> 8080\n\nHandling connection for 8080\n\n\ud83d\udca1 Se brancher directement sur un pod court-circuite le Service : si l'application répond ici et pas par le Service, le défaut est dans le Service (${cible}).`);return;
    }

    // kubectl get
    if(parts[0]==='kubectl'&&parts[1]==='get'){
      const res=parts[2];
      if(res==='pods'||res==='pod'||res==='po'){
        const isAll=parts.includes('-A')||parts.includes('--all-namespaces');
        const labelFlag=parts.findIndex(p=>p==='-l');
        const pods=npcs.filter(n=>!n.boss).map(n=>({
          name:n.name.toLowerCase().replace(/\s+/g,'-'),ns:zone,
          ready:questsDone[n.name]?'1/1':'0/1',
          status:questsDone[n.name]?'Running':'Pending',
          restarts:0,ip:`10.244.${Math.floor(Math.random()*5)}.${10+npcs.indexOf(n)}`,
          node:`worker-0${1+npcs.indexOf(n)%3}`
        }));
        const bossPods=npcs.filter(n=>n.boss).map(n=>({
          name:n.name.toLowerCase().replace(/\s+/g,'-'),ns:zone,
          ready:bossWins[n.bossId]?'0/1':'1/1',
          status:bossWins[n.bossId]?'Completed':'CrashLoopBackOff',
          restarts:bossWins[n.bossId]?0:42,ip:`10.244.9.${npcs.indexOf(n)}`,
          node:'worker-boss'
        }));
        const all=[...pods,...bossPods];
        const wide=parts.includes('-o')&&parts.includes('wide');
        let header=wide?'NAME                      READY   STATUS             RESTARTS   IP            NODE':'NAME                      READY   STATUS             RESTARTS   AGE';
        let rows=all.map(p=>{
          const nm=p.name.padEnd(26);const rdy=p.ready.padEnd(8);const st=p.status.padEnd(19);const rs=String(p.restarts).padEnd(11);
          return wide?`${nm}${rdy}${st}${rs}${p.ip.padEnd(14)}${p.node}`:`${nm}${rdy}${st}${rs}${Math.floor(Math.random()*48)+1}h`;
        });
        add('out',`${header}\n${rows.join('\n')}`);return;
      }
      if(res==='nodes'||res==='node'||res==='no'){
        add('out',`NAME         STATUS   ROLES           AGE    VERSION
master-01    Ready    control-plane   142d   v1.28.2
worker-01    Ready    <none>          142d   v1.28.2
worker-02    Ready    <none>          142d   v1.28.2
worker-03    Ready    <none>          140d   v1.28.2`);return;
      }
      if(res==='svc'||res==='services'||res==='service'){
        const portals=PORTALS[zone]||[];
        const svcs=portals.map(p=>`${p.to.padEnd(18)}ClusterIP   10.96.${Math.floor(Math.random()*255)}.${Math.floor(Math.random()*255)}   <none>   80/TCP   ${Math.floor(Math.random()*48)+1}h`);
        add('out',`NAME               TYPE        CLUSTER-IP       EXTERNAL-IP   PORT(S)  AGE\nkubernetes         ClusterIP   10.96.0.1        <none>        443/TCP  142d\n${svcs.join('\n')}`);return;
      }
      if(res==='ns'||res==='namespaces'||res==='namespace'){
        add('out',`NAME              STATUS   AGE
default           Active   142d
hub               Active   142d
pod-village       Active   142d
workload-fort     Active   142d
network-realm     Active   142d
arcane-sanctum    Active   142d
kube-system       Active   142d
kube-public       Active   142d`);return;
      }
      if(res==='deploy'||res==='deployments'||res==='deployment'){
        const deps=npcs.filter(n=>!n.boss).map(n=>`${n.name.toLowerCase().replace(/\s/g,'-').padEnd(24)}${questsDone[n.name]?'1/1':'0/1'}   1         ${questsDone[n.name]?1:0}          ${Math.floor(Math.random()*48)+1}h`);
        add('out',`NAME                      READY   UP-TO-DATE   AVAILABLE   AGE\n${deps.join('\n')}`);return;
      }
      if(res==='events'||res==='event'){
        const evList=events.filter(e=>e.zid===zone).map(e=>`Warning   ${e.name||e.t||'Incident'}   pod/${e.name?.toLowerCase().replace(/\s/g,'-')||'unknown'}   ${e.d}`);
        const enemyList=enemies.filter(e=>e.zid===zone).map(e=>`Warning   ${e.name}   pod/${e.name.toLowerCase().replace(/\s/g,'-')}   ${e.d}`);
        if(!evList.length&&!enemyList.length){add('out','No events found.');return;}
        add('out',`LAST SEEN   TYPE      REASON              OBJECT                MESSAGE\n${[...evList,...enemyList].join('\n')}`);return;
      }
      if(res==='endpoints'||res==='ep'){
        const target=parts[3];
        const eps=npcs.filter(n=>!n.boss&&questsDone[n.name]).map(n=>`10.244.1.${10+npcs.indexOf(n)}:8080`);
        add('out',`NAME         ENDPOINTS\n${zone.padEnd(13)}${eps.length?eps.join(','):'<none>'}`);return;
      }
      if(res==='ds'||res==='daemonsets'||res==='daemonset'){
        add('out',`NAME            DESIRED   CURRENT   READY   NODE SELECTOR   AGE
kube-proxy      4         4         4       <none>          142d
calico-node     4         4         4       <none>          142d`);return;
      }
      if(res==='jobs'||res==='job'){add('out','NAME            COMPLETIONS   DURATION   AGE\nbackup-etcd     1/1           12s        2h');return;}
      if(res==='pvc'){add('out',`NAME         STATUS   VOLUME       CAPACITY   ACCESS MODES   STORAGECLASS   AGE
data-db-0    Bound    pv-abc123    10Gi       RWO            gp3            7d
data-db-1    Bound    pv-def456    10Gi       RWO            gp3            7d`);return;}
      if(res==='ingressclass'||res==='ingressclasses'){add('out','NAME        CONTROLLER                  PARAMETERS   AGE\nnginx-new   k8s.io/ingress-nginx        <none>       30d');return;}
      if(res==='ingress'||res==='ing'){add('out','NAME        CLASS       HOSTS              ADDRESS      PORTS     AGE\napi-routes  nginx-new   api.company.com    10.0.1.50    80, 443   15d');return;}
      if(res==='netpol'||res==='networkpolicies'){add('out','NAME              POD-SELECTOR   AGE\nallow-frontend    app=api        5d');return;}
      if(res==='secret'||res==='secrets'){add('out','NAME                  TYPE                                  DATA   AGE\ndefault-token-abc     kubernetes.io/service-account-token   3      142d\ndb-credentials        Opaque                                2      7d');return;}
      if(res==='configmap'||res==='cm'){add('out','NAME               DATA   AGE\nkube-root-ca.crt   1      142d\napp-config         3      7d');return;}
      add('out',`error: the server doesn't have a resource type "${res||''}"`);return;
    }

    // kubectl describe
    if(parts[0]==='kubectl'&&parts[1]==='describe'){
      const npc=npcs.find(n=>cmd.toLowerCase().includes(n.name.toLowerCase().replace(/\s/g,'-')));
      if(npc){add('out',`Name:         ${npc.name.toLowerCase().replace(/\s/g,'-')}
Namespace:    ${zone}
Status:       ${questsDone[npc.name]?'Running':'Pending'}
IP:           10.244.1.${10+npcs.indexOf(npc)}
Node:         worker-0${1+npcs.indexOf(npc)%3}
Labels:       app=${npc.name.toLowerCase().replace(/\s/g,'-')}
              role=${npc.boss?'boss':'npc'}
Containers:
  main:
    Image:    kubeworld/${npc.name.toLowerCase().replace(/\s/g,'-')}:latest
    State:    ${questsDone[npc.name]?'Running':'Waiting'}
    Ready:    ${questsDone[npc.name]?'True':'False'}
Events:
  Normal   Scheduled  default-scheduler  Successfully assigned
  Normal   Pulled     kubelet            Container image pulled`);return;}
      add('out','Error: pods "'+( parts[3]||'?')+'" not found');return;
    }

    // kubectl logs
    if(parts[0]==='kubectl'&&parts[1]==='logs'){
      const prev=parts.includes('--previous')||parts.includes('-p');
      if(prev){add('out','[2024-03-15T03:01:44Z] [ERROR] Connection refused: postgres.database.svc:5432\n[2024-03-15T03:01:44Z] [FATAL] Cannot start without database connection\n[2024-03-15T03:01:44Z] [INFO]  Shutting down with exit code 1');return;}
      add('out','[2024-03-15T10:00:01Z] [INFO]  Server started on port 8080\n[2024-03-15T10:00:01Z] [INFO]  Connected to database\n[2024-03-15T10:00:02Z] [INFO]  Ready to accept connections\n[2024-03-15T10:00:15Z] [INFO]  GET /healthz 200 1ms\n[2024-03-15T10:00:30Z] [INFO]  GET /api/v1/pods 200 45ms');return;
    }

    // kubectl top
    if(parts[0]==='kubectl'&&parts[1]==='top'){
      const pods=npcs.filter(n=>!n.boss).map(n=>`${n.name.toLowerCase().replace(/\s/g,'-').padEnd(26)}${Math.floor(Math.random()*200)+10}m     ${Math.floor(Math.random()*256)+64}Mi`);
      add('out',`NAME                        CPU(cores)   MEMORY(bytes)\n${pods.join('\n')}`);return;
    }

    // kubectl auth can-i
    if(parts[0]==='kubectl'&&parts[1]==='auth'&&parts[2]==='can-i'){
      const verb=parts[3]||'get';const resource=parts[4]||'pods';
      add('out',inv.length>5?'yes':'no — RBAC: access denied');return;
    }

    // kubectl rollout
    if(parts[0]==='kubectl'&&parts[1]==='rollout'){
      if(parts[2]==='status')add('out','deployment "web" successfully rolled out');
      else if(parts[2]==='undo')add('out','deployment.apps/web rolled back to revision 2');
      else if(parts[2]==='history')add('out','REVISION  CHANGE-CAUSE\n1         image=nginx:1.19\n2         image=nginx:1.20\n3         image=nginx:1.21');
      else add('out','Available commands: status, undo, history, restart');
      return;
    }

    // etcdutl
    if(parts[0]==='etcdutl'||parts[0]==='etcdctl'){
      if(cmd.includes('snapshot status'))add('out','Hash:      3c5a174b\nRevision:  18456\nTotal Keys: 1247\nTotal Size: 5.2 MB');
      else if(cmd.includes('snapshot restore'))add('out','Snapshot restored to /var/lib/etcd-restored\nMember name: default');
      else if(cmd.includes('snapshot save'))add('out','Snapshot saved at /backup/etcd-snapshot.db');
      else add('out','Available: snapshot save, snapshot status, snapshot restore');
      return;
    }

    // kubectl exec
    if(parts[0]==='kubectl'&&parts[1]==='exec'){
      if(cmd.includes('nslookup'))add('out','Server:    10.96.0.10\nAddress:   10.96.0.10#53\n\nName:      api.backend.svc.cluster.local\nAddress:   10.96.42.15');
      else if(cmd.includes('/bin/sh')||cmd.includes('bash'))add('out','# (shell simulé — tape "exit" pour sortir)\nroot@pod:/# ls\napp  bin  etc  home  lib  proc  run  tmp  var');
      else add('out','error: you must specify at least one command for the container');
      return;
    }

    // kubectl debug
    if(parts[0]==='kubectl'&&parts[1]==='debug'){
      const target=parts[2]||'<pod-name>';
      add('out',`Defaulting debug container name to debugger-xyz.
If you don't see a command prompt, try pressing enter.
root@${target}:/#
# Conteneur éphémère attaché au pod !
# Utile pour inspecter un pod sans modifier son image.
# Exemples courants :
#   kubectl debug -it <pod> --image=busybox --target=<container>
#   kubectl debug node/<node> -it --image=ubuntu`);
      return;
    }

    // kubectl get pvc/pv
    if(parts[0]==='kubectl'&&parts[1]==='get'&&(parts[2]==='pvc'||parts[2]==='persistentvolumeclaim')){
      add('out',`NAME    STATUS  VOLUME    CAPACITY  ACCESS MODES  STORAGECLASS
data    Bound   pv-data   50Gi      RWO           standard
logs    Bound   pv-logs   10Gi      RWO           standard`);return;
    }
    if(parts[0]==='kubectl'&&parts[1]==='get'&&(parts[2]==='pv'||parts[2]==='persistentvolume')){
      add('out',`NAME      CAPACITY  ACCESS MODES  RECLAIM POLICY  STATUS  STORAGECLASS
pv-data   50Gi      RWO           Retain          Bound   standard
pv-logs   10Gi      RWO           Delete          Bound   standard`);return;
    }
    if(parts[0]==='kubectl'&&parts[1]==='get'&&(parts[2]==='storageclass'||parts[2]==='sc')){
      add('out',`NAME                 PROVISIONER                    RECLAIMPOLICY  VOLUMEBINDINGMODE
standard (default)   rancher.io/local-path           Delete         WaitForFirstConsumer`);return;
    }

    // kubectl apply
    if(parts[0]==='kubectl'&&parts[1]==='apply'){
      if(parts.includes('-f')){const fname=parts[parts.indexOf('-f')+1]||'manifest.yaml';add('out',`${fname} configured\nservice/app unchanged\ndeployment.apps/app configured`);}
      else add('err','error: must specify -f <filename>');return;}

    // kubectl delete
    if(parts[0]==='kubectl'&&parts[1]==='delete'){
      const res=parts[2]||'';const name=parts[3]||'';
      if(res==='pod'||res==='pods'){add('out',`pod "${name||'app-xyz'}" deleted`);}
      else if(res==='deploy'||res==='deployment'){add('out',`deployment.apps "${name||'app'}" deleted`);}
      else if(res==='svc'||res==='service'){add('out',`service "${name||'my-svc'}" deleted`);}
      else add('out',`${res} "${name}" deleted`);return;}

    // kubectl scale
    if(parts[0]==='kubectl'&&parts[1]==='scale'){
      const replicas=parts.find(p=>p.startsWith('--replicas='));
      const n=replicas?replicas.split('=')[1]:'3';
      add('out',`deployment.apps/${parts[3]||'app'} scaled to ${n} replicas`);return;}

    // kubectl cordon/uncordon
    if(parts[0]==='kubectl'&&(parts[1]==='cordon'||parts[1]==='uncordon')){
      const node=parts[2]||'worker-01';
      if(parts[1]==='cordon')add('out',`node/${node} cordoned\n\n$ kubectl get nodes\nNAME         STATUS                     ROLES\n${node}    Ready,SchedulingDisabled   <none>`);
      else add('out',`node/${node} uncordoned`);return;}

    // kubectl taint
    if(parts[0]==='kubectl'&&parts[1]==='taint'){
      const node=parts[2]||'';const taint=parts[3]||'';
      if(taint.endsWith('-'))add('out',`node/${node||'worker-01'} untainted`);
      else add('out',`node/${node||'worker-01'} tainted`);return;}

    // kubectl get componentstatuses
    if(parts[0]==='kubectl'&&parts[1]==='get'&&(parts[2]==='cs'||parts[2]==='componentstatuses')){
      add('out',`Warning: v1 ComponentStatus is deprecated\nNAME                 STATUS    MESSAGE\nscheduler            Healthy   ok\ncontroller-manager   Healthy   ok\netcd-0               Healthy   ok`);return;}

    // kubectl create
    if(parts[0]==='kubectl'&&parts[1]==='create'){
      if(cmd.includes('configmap'))add('out','configmap/app-config created');
      else if(cmd.includes('secret'))add('out','secret/db-credentials created');
      else if(cmd.includes('deploy'))add('out',`deployment.apps/${parts[3]||'app'} created`);
      else add('out','Available: configmap, secret, deployment, service, job, ...');
      return;
    }

    // kubectl run with dry-run
    if(parts[0]==='kubectl'&&parts[1]==='run'){
      if(cmd.includes('--dry-run')){add('out',`apiVersion: v1\nkind: Pod\nmetadata:\n  name: ${parts[2]||'nginx'}\n  labels:\n    run: ${parts[2]||'nginx'}\nspec:\n  containers:\n  - name: ${parts[2]||'nginx'}\n    image: ${parts.find(p=>p.startsWith('--image='))?.split('=')[1]||'nginx'}\n    resources: {}`);return;}
      add('out',`pod/${parts[2]||'nginx'} created`);return;
    }

    add('err',`error: unknown command "${parts.slice(1).join(' ')}"\nRun 'help' for usage.`);
  },[misUI,zone,inv,questsDone,bossWins,xp,hearts,events,enemies,pos,dailyDone,dailyChallenge]);

  // Flashcard generator
  const genFlashcard=useCallback(()=>{
    const allCards=[
      {front:"Combien d'IPs a un Pod avec 3 conteneurs ?",back:"1 seule IP ! Les conteneurs partagent le namespace réseau."},
      {front:"Exit Code 137 signifie ?",back:"OOMKilled — le kernel a tué le conteneur (128 + signal 9 SIGKILL)"},
      {front:"Que fait kubectl rollout undo ?",back:"Revient à la révision précédente du Deployment"},
      {front:"ClusterRole + RoleBinding = permissions où ?",back:"Limitées au namespace du RoleBinding uniquement"},
      {front:"Endpoints vides sur un Service signifie ?",back:"Selector mismatch — le Service ne trouve aucun Pod"},
      {front:"readinessProbe FAIL → que se passe-t-il ?",back:"Pod retiré des Endpoints (plus de trafic). Pas de restart !"},
      {front:"Les Secrets K8s sont chiffrés ?",back:"NON ! Encodés en base64 seulement. Utiliser Vault/SealedSecrets."},
      {front:"volumeClaimTemplate d'un StatefulSet est modifiable ?",back:"NON ! IMMUTABLE. Il faut supprimer et recréer le StatefulSet."},
      {front:"Quel composant est le SEUL à parler à etcd ?",back:"kube-apiserver. Tout passe par l'API server."},
      {front:"DB check dans livenessProbe → danger ?",back:"OUI ! Si DB down → cascade de restarts → thundering herd"},
      {front:"DaemonSet garantit quoi ?",back:"Exactement 1 pod par node. Automatiquement sur les nouveaux nodes."},
      {front:"DNS cross-namespace format ?",back:"<service>.<namespace>.svc.cluster.local"},
      {front:"PVC en Pending — cause probable ?",back:"Aucune StorageClass disponible, ou aucun PV avec la capacité/access mode demandés."},
      {front:"Différence RWO / RWX ?",back:"RWO = 1 seul node R/W. RWX = plusieurs nodes simultanément (NFS, CephFS...)."},
      {front:"kubectl debug sert à ?",back:"Attacher un conteneur éphémère à un pod pour debugger sans modifier l'image."},
      {front:"HPA nécessite quoi pour fonctionner ?",back:"metrics-server installé dans le cluster. Sans lui, TARGETS = unknown."},
    ];
    // Prioritize concepts from incomplete quests
    return pickRandom(allCards);
  },[]);

  useEffect(()=>{if(!loaded)return;const iv=setInterval(()=>setGt(t=>(t+1)%1440),2500);return()=>clearInterval(iv);},[loaded]);
  useEffect(()=>{if(!loaded)return;const iv=setInterval(()=>setHearts(h=>Math.min(3,h+1)),90000);return()=>clearInterval(iv);},[loaded]);

  // Keep posRef in sync
  useEffect(()=>{posRef.current=pos;},[pos]);

  // ⭐ Ou faire apparaitre quelque chose de vivant : dans un anneau autour du joueur,
  // a la limite de son champ de vision. Avant, on tirait une case au hasard sur toute la
  // carte ; l'ecran ne montre que 22x15 cases sur 40x24, donc plus de la moitie des
  // apparitions se faisaient hors de vue et n'etaient jamais rencontrees. C'est ca qui
  // rendait les zones vides, bien plus que leur taille.
  const caseAutour=useCallback((map,p,min,max)=>{
    for(let a=0;a<60;a++){
      const ang=Math.random()*Math.PI*2,d=min+Math.random()*(max-min);
      const x=Math.round(p.x+Math.cos(ang)*d),y=Math.round(p.y+Math.sin(ang)*d);
      if(!isWalk(map[y]?.[x]))continue;
      if((NPCS[zone]||[]).some(n=>n.x===x&&n.y===y))continue;
      return{x,y};
    }
    return null;
  },[zone]);

  // Spawn zone enemies
  useEffect(()=>{if(!loaded)return;const iv=setInterval(()=>{setEnemies(en=>{if(en.length>=Math.max(2,Math.round(SURFACES[zone]/220)))return en;const pool=ZONE_ENEMIES[zone]||[];if(!pool.length)return en;const map=MAPS[zone];const et=pickRandom(pool);const c=caseAutour(map,posRef.current,7,13);if(!c)return en;return[...en,{...et,x:c.x,y:c.y,seed:Math.random()*100,zid:zone,xp:20+Math.floor(Math.random()*15)}];});},12000);return()=>clearInterval(iv);},[zone,loaded,caseAutour]);

  // Move enemies
  useEffect(()=>{if(!loaded)return;const iv=setInterval(()=>{const map=MAPS[zone];setEnemies(ens=>ens.map(e=>{if(e.zid!==zone)return e;const dirs=[[0,-1],[0,1],[-1,0],[1,0]];const[dx,dy]=pickRandom(dirs);const nx=e.x+dx,ny=e.y+dy;if(isWalk(map[ny]?.[nx]))return{...e,x:nx,y:ny};return e;}));},1500);return()=>clearInterval(iv);},[zone,loaded]);

  // Spawn chests — sequential, never repeat
  useEffect(()=>{if(!loaded||chests.length>0)return;const map=MAPS[zone];const newC=[];const usedIndices=Object.values(chestsOpened).filter(v=>typeof v==='number');const available=CHEST_LOOT.map((_,i)=>i).filter(i=>!usedIndices.includes(i));for(let i=0;i<Math.min(2,available.length);i++){for(let a=0;a<30;a++){const cx=2+Math.floor(Math.random()*(map[0].length-4)),cy=2+Math.floor(Math.random()*(map.length-4));if(isWalk(map[cy]?.[cx])&&!NPCS[zone]?.some(n=>n.x===cx&&n.y===cy)&&!newC.some(c=>c.x===cx&&c.y===cy)){newC.push({x:cx,y:cy,loot:CHEST_LOOT[available[i]],lootIdx:available[i],id:`${zone}-${cx}-${cy}`});break;}}}setChests(newC);},[zone,loaded]);

  // Enemy collision
  useEffect(()=>{if(!loaded||dialogUI||questUI||bossUI||termUI||overlay||inHouse||termOpen||showMap||showFlash)return;
    const enemy=enemies.find(e=>e.zid===zone&&e.x===pos.x&&e.y===pos.y);
    if(enemy){playSound('hit');triggerShake(6);setTerm({ev:enemy,input:'',fails:0,result:null,isEnemy:true});setEnemies(en=>en.filter(e=>e!==enemy));setTimeout(()=>iRef.current?.focus(),100);}
  },[pos,enemies,zone,loaded,dialogUI,questUI,bossUI,termUI,overlay,inHouse]);

  // Spawn incidents
  useEffect(()=>{if(!loaded)return;const iv=setInterval(()=>{if(events.length>=Math.max(2,Math.round(SURFACES[zone]/300))||inHouse)return;const pool=ZONE_ENEMIES[zone]||[];if(!pool.length)return;const map=MAPS[zone];const inc=pickRandom(pool);const c=caseAutour(map,posRef.current,5,11);if(c)setEvents(ev=>[...ev,{...inc,x:c.x,y:c.y,zid:zone,icon:'⚠️'}]);},15000);return()=>clearInterval(iv);},[zone,pos,events,loaded,inHouse,caseAutour]);

  const canAccess=useCallback(zid=>{const r=ZONE_REQ[zid];const lv=getLv(xp);if(lv.l<r.l)return{ok:false,msg:`🔒 Level ${r.l} requis (Lv${lv.l})`};if(r.b&&!bossWins[r.b])return{ok:false,msg:`🔒 Bats ${BOSSES[r.b]?.name} !`};return{ok:true};},[xp,bossWins]);

  const changeZone=(to,sx,sy)=>{
    playSound('portal');
    const intro=ZONE_INTROS[to];
    setZoneTransition({zone:to,text:intro?.title||to,sub:intro?.sub||'',start:Date.now()});
    setTimeout(()=>{
      setZone(to);setPos({x:sx,y:sy});setDir('down');compP.current={x:sx,y:sy};setEvents([]);setEnemies([]);setChests([]);
      // Show zone intro on first visit
      if(!zonesVisited[to]){
        setZonesVisited(v=>{const next={...v,[to]:true};try{localStorage.setItem('kw6_visited',JSON.stringify(next));}catch{}return next;});
        if(intro)setTimeout(()=>setZoneIntroShow(intro),600);
      }
      setTimeout(()=>setZoneTransition(null),800);
    },500);
  };

  const interact=useCallback(()=>{
    const curInt=inHouse?.intMap||INTERIOR_MAP;
    const map=inHouse?curInt:MAPS[zone];
    const fx=pos.x+(dir==='right'?1:dir==='left'?-1:0),fy=pos.y+(dir==='down'?1:dir==='up'?-1:0);

    // In house: talk to NPC or exit
    if(inHouse){
      const hnpc=inHouse.house.npcs?.find(n=>Math.abs(n.rx-fx)<=1&&Math.abs(n.ry-fy)<=1);
      if(hnpc){setDlg({npc:hnpc,idx:0,isHouse:true});return;}
      // Exit: walk to door
      if(curInt[fy]?.[fx]==='D'||fy>=curInt.length-1){setInHouse(null);setPos(inHouse.exitPos);return;}
      return;
    }

    // Check houses
    const iMaison=(HOUSES[zone]||[]).findIndex(h=>h.dx===fx&&h.dy===fy);
    const house=iMaison>=0?HOUSES[zone][iMaison]:null;
    if(house){
      // ⚠️ Les deux portes d'une zone menaient a la MEME piece : meme forme, meme
      // mobilier, seuls les habitants changeaient. Chaque maison a desormais la sienne.
      const intMap=(iMaison===1&&INTERIOR_MAPS_B[zone])||INTERIOR_MAPS[zone]||INTERIOR_MAP;
      // Et on pose les habitants sur des cases REELLEMENT libres : une position calculee
      // a l'aveugle (3, 7, 11...) tombe dans un meuble des que la piece change de forme.
      const libres=[];
      for(let ry=2;ry<intMap.length-2;ry++)for(let rx=2;rx<intMap[0].length-2;rx++)
        if(isWalk(intMap[ry][rx]))libres.push({rx,ry});
      // Repartis dans la largeur de la piece : prendre les premieres cases libres les
      // entassait toutes dans le coin en haut a gauche.
      libres.sort((a,b)=>a.rx-b.rx||a.ry-b.ry);
      const nH=Math.max(1,house.npcs.length);
      const npcsWithPos=house.npcs.map((n,i)=>({...n,...(libres[Math.floor((i+0.5)*libres.length/nH)]||{rx:2,ry:2})}));
      setInHouse({house:{...house,npcs:npcsWithPos},exitPos:{x:pos.x,y:pos.y},intMap});
      setPos({x:Math.floor(intMap[0].length/2),y:intMap.length-2});setDir('up');return;
    }

    // NPCs
    const npc=(NPCS[zone]||[]).find(n=>(n.x===fx&&n.y===fy)||(n.x===pos.x&&n.y===pos.y));
    if(npc){
      if(npc.boss){
        if(bossWins[npc.bossId]){nfy("✅ Déjà vaincu !");return;}
        if(hearts<=0){nfy("💔 Plus de cœurs !");return;}
      }
      setDlg({npc,idx:0});return;
    }

    // Chests
    const chest=chests.find(c=>c.x===fx&&c.y===fy);
    if(chest){if(chestsOpened[chest.id]){nfy('📦 Déjà ouvert !');return;}playSound('chest');setCO(co=>({...co,[chest.id]:chest.lootIdx}));setChests(cs=>cs.filter(c=>c.id!==chest.id));setJnl(j=>[...j,{t:'Coffre',c:chest.loot,i:'📦'}]);nfy('📦 Coffre trouvé !');return;}

    // Events
    const evt=events.find(e=>e.zid===zone&&((e.x===fx&&e.y===fy)||(e.x===pos.x&&e.y===pos.y)));
    if(evt){setTerm({ev:evt,input:'',fails:0,result:null});setTimeout(()=>iRef.current?.focus(),100);}
  },[zone,pos,dir,events,bossWins,hearts,inHouse,chests,chestsOpened,questsDone]);

  // Dialog advance
  const advDlg=()=>{
    if(!dialogUI)return;const{npc,isHouse,accueil}=dialogUI;
    {
      setDlg(null);
      if(accueil)return;
      if(isHouse){setJnl(j=>{if(j.some(e=>e.t===npc.name))return j;return[...j,{t:npc.name,c:npc.lines.join('\n'),i:npc.icon}];});return;}
      if(npc.boss&&npc.bossId&&!bossWins[npc.bossId]){
        const boss=BOSSES[npc.bossId];
        if(boss.intro){setBossIntro({boss,id:npc.bossId,idx:0});}
        else{setBossArena(npc.bossId);setBoss({b:boss,id:npc.bossId,phase:0,input:'',fails:0,result:null});setTimeout(()=>iRef.current?.focus(),200);}
      }else if(npc.mission){
        const m=MISSIONS[npc.mission];
        setMis({m,npc,phase:0,fails:0,result:null,rev:!!questsDone[npc.name]});
        // La mission se joue dans le VRAI terminal du jeu : on l'ouvre, et on prete au
        // joueur les commandes dont il aura besoin, sinon le verrou d'inventaire le
        // bloquerait sur sa propre mission.
        setInv(i=>{const next=[...i];(m.outils||[]).forEach(c=>{if(!next.includes(c))next.push(c);});
          if(next.length>=12)checkAch('kubectl_master');return next;});
        setTermOpen('split');
        setTermHist(h=>[...h,{type:'sys',text:`${m.icon} ${m.name} - etape 1/${m.phases.length}. Tape la commande ici.`}].slice(-100));
        setTimeout(()=>termRef2.current?.focus(),150);
      }else if(npc.pool){
        const rev=!!questsDone[npc.name];
        const{qi,q,fresh}=pickQuestion(npc);
        setQst({npc,q,qi,rev,fresh,answer:null,lesIdx:q.les?0:null});
      }
    }
  };

  // Tire une question du pool, en priorite une que le joueur n'a JAMAIS vue.
  // Sans ca un PNJ valide emportait avec lui les 2 ou 3 questions de son pool que le
  // tirage aleatoire n'avait jamais sorties : la majorite du contenu restait invisible.
  const pickQuestion=(npc)=>{
    const seen=seenQ[npc.name]||[];
    const all=npc.pool.map((_,i)=>i);
    const fresh=all.filter(i=>!seen.includes(i));
    const from=fresh.length?fresh:all;
    const qi=from[Math.floor(Math.random()*from.length)];
    return{qi,q:npc.pool[qi],fresh:fresh.length>0};
  };

  const completeQuest=(npc,q,ui)=>{
    playSound('quest');
    if(ui&&ui.qi!=null)setSeenQ(sq=>{const prev=sq[npc.name]||[];return prev.includes(ui.qi)?sq:{...sq,[npc.name]:[...prev,ui.qi]};});
    const note=()=>setJnl(j=>j.some(e=>e.t===npc.name&&e.c===q.exp)?j:[...j,{t:npc.name,c:q.exp,i:npc.icon}]);
    if(ui&&ui.rev){ // revision : la quete est deja faite, seule une question inedite rapporte
      if(ui.fresh){addXP(10);note();nfy('📖 Nouvelle question apprise');}
      else nfy('📖 Révision');
      return;
    }
    setQD(d=>{const next={...d,[npc.name]:true};
      if(Object.keys(next).length===1)checkAch('first_quest');
      const allNpcs=Object.values(NPCS).flat().filter(n=>!n.boss&&n.pool);
      if(allNpcs.every(n=>next[n.name]))checkAch('completionist');
      return next;});
    addXP(25);
    if(npc.gives&&!inv.includes(npc.gives)){setInv(i=>{const next=[...i,npc.gives];if(next.length>=12)checkAch('kubectl_master');return next;});}
    note();
  };

  // Hauteur du bandeau du haut. Une seule definition : une etiquette calee sur une
  // valeur recopiee finit toujours par passer dessous.
  const HUD_H=24;

  const advRef=useRef(null);advRef.current=advDlg;

  // Keys
  useEffect(()=>{
    const d=e=>{const k=e.key.toLowerCase();keysR.current[k]=true;if(e.code)keysR.current[e.code]=true;
      if(k==='escape'){setDlg(null);setQst(null);setMis(null);if(termUI){if(termUI.result==='ok'&&!termUI.isEnemy)setEvents(ev=>ev.filter(x=>!(x.x===termUI.ev.x&&x.y===termUI.ev.y)));setTerm(null);}if(bossUI){playSound('hit');triggerShake();setHearts(h=>Math.max(0,h-1));setBoss(null);setBossArena(null);}setBossIntro(null);setOv(null);setRecap(null);setTermOpen(null);setShowMap(false);setShowFlash(false);setZoneIntroShow(null);if(inHouse){setInHouse(null);setPos(inHouse.exitPos);}return;}
      if(dialogUI&&!e.repeat&&(k==='e'||k===' '||k==='enter')){e.preventDefault();advRef.current();return;}
      const any=dialogUI||questUI||bossUI||misUI||termUI||overlay||recap||showMap||showFlash;
      if(termOpen&&k!=='escape'&&k!=='²')return; // let terminal handle all keys except Escape/²
      if(!any&&!inHouse&&!termOpen){
        if(k==='i')setOv('inv');if(k==='j')setOv('jnl');if(k==='m')setOv('menu');if(k==='p')setOv('ach');
        if(k==='t'||k==='²'){setTermOpen('split');setTimeout(()=>termRef2.current?.focus(),100);}
        if(k==='tab'){e.preventDefault();setShowMap(true);}
        if(k==='f'){setFlashCard(genFlashcard());setShowFlash(true);}
      }
      if(termOpen&&k==='²'){setTermOpen(null);}
    };
    const u=e=>{keysR.current[e.key.toLowerCase()]=false;if(e.code)keysR.current[e.code]=false;};
    window.addEventListener('keydown',d);window.addEventListener('keyup',u);
    return()=>{window.removeEventListener('keydown',d);window.removeEventListener('keyup',u);};
  },[dialogUI,questUI,bossUI,termUI,overlay,recap,inHouse,termOpen,showMap,showFlash,genFlashcard]);

  // Game loop
  useEffect(()=>{
    if(!loaded||!tuiles)return;const canvas=cvs.current;if(!canvas)return;const ctx=canvas.getContext('2d');
    ctx.imageSmoothingEnabled=false;
    canvas.width=CW;canvas.height=CH;let af;
    const loop=()=>{
      tickR.current++;const tick=tickR.current;
      // Decrement screen shake
      if(shakeRef.current>0)shakeRef.current--;
      const curInt=inHouse?.intMap||INTERIOR_MAP;
      const arenaMap=bossArena?BOSS_ARENAS[bossArena]:null;
      const map=arenaMap||(inHouse?curInt:MAPS[zone]);
      const mapH=map.length,mapW=map[0].length;
      const nA=inHouse?0:getNight(gt);
      const npcs=inHouse?[]:NPCS[zone]||[];
      const anyOv=dialogUI||questUI||bossUI||misUI||termUI||overlay||recap||termOpen||showMap||showFlash;

      if(!anyOv&&cdR.current<=0){
        const k=keysR.current;let dx=0,dy=0,nd=dir;
        if(k['ArrowUp']||k['KeyW']){dy=-1;nd='up';}else if(k['ArrowDown']||k['KeyS']){dy=1;nd='down';}
        else if(k['ArrowLeft']||k['KeyA']){dx=-1;nd='left';}else if(k['ArrowRight']||k['KeyD']){dx=1;nd='right';}
        if(dx||dy){setDir(nd);const nx=pos.x+dx,ny=pos.y+dy;const ch=map[ny]?.[nx];
          const npcBlock=npcs.some(n=>n.x===nx&&n.y===ny);
          if(ch&&(isWalk(ch)||ch==='D')&&!npcBlock){
            setPos({x:nx,y:ny});setWalk(true);cdR.current=6;
            if(!inHouse){compP.current={x:pos.x,y:pos.y};
              // Portal check
              const portal=(PORTALS[zone]||[]).find(p=>p.x===nx&&p.y===ny);
              if(portal){const ac=canAccess(portal.to);if(ac.ok)setTimeout(()=>changeZone(portal.to,portal.sx,portal.sy),100);
                else{setPMsg(ac.msg);setTimeout(()=>setPMsg(''),2500);}}
            }else{
              // House exit via movement (walking into door tile at bottom row)
              if(ch==='D'&&ny>=curInt.length-1&&inHouse){setInHouse(null);setPos(inHouse.exitPos);}
            }
          }else cdR.current=4;
        }else setWalk(false);
        if(k['KeyE']||k['Space']){interact();k['KeyE']=false;k['Space']=false;k['e']=false;k[' ']=false;cdR.current=10;}
      }
      if(cdR.current>0)cdR.current--;

      const camX=inHouse?0:Math.max(0,Math.min(pos.x-Math.floor(VW/2),mapW-VW));
      const camY=inHouse?0:Math.max(0,Math.min(pos.y-Math.floor(VH/2),mapH-VH));
      ctx.clearRect(0,0,CW,CH);

      if(bossArena&&arenaMap){
        // ═══ BOSS ARENA RENDERING ═══
        ctx.fillStyle='#0a0a0a';ctx.fillRect(0,0,CW,CH);
        const ox=Math.floor((VW-arenaMap[0].length)/2),oy=Math.floor((VH-arenaMap.length)/2);
        for(let ty=0;ty<arenaMap.length;ty++)for(let tx=0;tx<arenaMap[0].length;tx++){
          dTile(ctx,ox+tx,oy+ty,arenaMap[ty][tx],tick,0,zone,{h:arenaMap[ty-1]?.[tx],b:arenaMap[ty+1]?.[tx],g:arenaMap[ty]?.[tx-1],d:arenaMap[ty]?.[tx+1],hg:arenaMap[ty-1]?.[tx-1],hd:arenaMap[ty-1]?.[tx+1],ax:tx,ay:ty});
        }
        // Boss sprite in center
        const bossD=BOSSES[bossArena];
        const bx=(ox+Math.floor(arenaMap[0].length/2))*T,by=(oy+3)*T;
        const bp=Math.sin(tick*0.08)*0.4+0.6;
        ctx.fillStyle=`rgba(255,40,40,${bp*0.3})`;ctx.beginPath();ctx.arc(bx+T/2,by+T/2,T*1.5,0,Math.PI*2);ctx.fill();
        ctx.fillStyle='#ff2222';ctx.fillRect(bx+4,by+6,T-8,T-6);ctx.fillStyle='#f0c8a0';ctx.fillRect(bx+6,by,T-12,10);
        ctx.font='20px serif';ctx.textAlign='center';ctx.fillText(bossD.icon||'💀',bx+T/2,by-6);
        // Boss health bar
        const totalPhases=bossD.phases.length;const donePhases=bossUI?bossUI.phase+(bossUI.result==='ok'?1:0):0;
        const hpW=120;ctx.fillStyle='#1a1a1a';ctx.fillRect(bx+T/2-hpW/2,by-22,hpW,8);
        ctx.fillStyle='#ff4444';ctx.fillRect(bx+T/2-hpW/2,by-22,hpW*(1-donePhases/totalPhases),8);
        ctx.strokeStyle='#ff6b6b';ctx.strokeRect(bx+T/2-hpW/2,by-22,hpW,8);
        // Arena HUD
        ctx.fillStyle='rgba(0,0,0,0.8)';ctx.fillRect(0,0,CW,24);
        ctx.fillStyle='#ff4444';ctx.font='bold 13px monospace';ctx.textAlign='center';
        ctx.fillText(`⚔️ ${bossD.name} — Phase ${(bossUI?.phase||0)+1}/${totalPhases}`,CW/2,16);
      }else if(inHouse){
        // ═══ INTERIOR RENDERING (zone-themed) ═══
        ctx.fillStyle='#1a1a2a';ctx.fillRect(0,0,CW,CH);
        const ox=Math.floor((VW-mapW)/2),oy=Math.floor((VH-mapH)/2);
        for(let ty=0;ty<mapH;ty++)for(let tx=0;tx<mapW;tx++){
          dTile(ctx,ox+tx,oy+ty,map[ty][tx],tick,0,zone,{h:map[ty-1]?.[tx],b:map[ty+1]?.[tx],g:map[ty]?.[tx-1],d:map[ty]?.[tx+1],hg:map[ty-1]?.[tx-1],hd:map[ty-1]?.[tx+1],ax:tx,ay:ty});
        }
        // House NPCs
        inHouse.house.npcs?.forEach(n=>{
          // ⚠️ A l'interieur, les habitants etaient trois rectangles de couleur, alors
          // qu'ils ont un vrai sprite dehors : on ne les prenait pas pour des personnes
          // (regle R9). Meme dessin dedans que dehors.
          const sx=(ox+n.rx)*T,sy=(oy+n.ry)*T;
          dSprite(ctx,ox+n.rx,oy+n.ry,'npc',{...n,x:n.rx,y:n.ry},tick);
          if(Math.abs(n.rx-pos.x)<=2&&Math.abs(n.ry-pos.y)<=2&&!anyOv){
            ctx.fillStyle='#ffd700';ctx.font='bold 9px monospace';ctx.textAlign='center';
            ctx.fillText('[E]',sx+T/2,sy+T+9);}
        });
        // Player in house
        dSprite(ctx,ox+pos.x,oy+pos.y,'player',{dir,walk},tick);
        // Interior HUD
        ctx.fillStyle='rgba(0,0,0,0.7)';ctx.fillRect(0,0,CW,28);
        ctx.fillStyle='#ffd700';ctx.font='bold 12px monospace';ctx.textAlign='center';
        const houseName=inHouse.house.name||'Intérieur';
        ctx.fillText(`🏠 ${houseName}`,CW/2,12);
        ctx.fillStyle='#888';ctx.font='10px monospace';ctx.fillText('Échap pour sortir',CW/2,24);
      }else{
        // ═══ NORMAL ZONE RENDERING ═══
        for(let ty=0;ty<=VH;ty++)for(let tx=0;tx<=VW;tx++)dTile(ctx,tx,ty,map[camY+ty]?.[camX+tx]||'#',tick,nA,zone,{h:map[camY+ty-1]?.[camX+tx],b:map[camY+ty+1]?.[camX+tx],g:map[camY+ty]?.[camX+tx-1],d:map[camY+ty]?.[camX+tx+1],hg:map[camY+ty-1]?.[camX+tx-1],hd:map[camY+ty-1]?.[camX+tx+1],ax:camX+tx,ay:camY+ty});
        // Les halos de nuit, en une passe : dessines tuile par tuile ils se decoupaient
        // en demi-cercles gris (defaut signale sur capture).
        dessinerHalos(ctx,map,camX,camY,VW,VH,nA,T);

        // Portals
        (PORTALS[zone]||[]).forEach(p=>{if(p.x>=camX&&p.x<camX+VW&&p.y>=camY&&p.y<camY+VH){
          dSprite(ctx,p.x-camX,p.y-camY,'portal',{},tick);
          const ac=canAccess(p.to);if(!ac.ok){ctx.fillStyle='rgba(255,255,255,0.85)';ctx.font='bold 14px serif';ctx.textAlign='center';ctx.fillText('🔒',(p.x-camX)*T+T/2,(p.y-camY)*T+T/2+5);}
          // ⚠️ Le cadre se mesure sur le texte, et cadre et texte se deplacent ENSEMBLE :
          // sinon un portail au bord de l'ecran laisse sa boite d'un cote et son nom de
          // l'autre. Et l'etiquette reste sous le bandeau du HUD (24 px), sans quoi un
          // portail en haut de carte ecrit son nom derriere la barre de vie.
          ctx.font='bold 10px monospace';ctx.textAlign='left';
          // ⚠️ measureText sous-estime les emoji : le nom debordait de son cadre et
          // sortait de l'ecran. On compte un supplement par caractere hors ASCII.
          const horsAscii=[...p.label].filter(c=>c.codePointAt(0)>127).length;
          const lw=Math.ceil(ctx.measureText(p.label).width)+14+horsAscii*7,lh=16;
          const lx=Math.min(Math.max((p.x-camX)*T+T/2-lw/2,3),CW-lw-3);
          const ly=Math.min(Math.max((p.y-camY)*T-lh-4,HUD_H+3),CH-lh-3);
          ctx.fillStyle='rgba(8,10,14,0.88)';ctx.fillRect(lx,ly,lw,lh);
          ctx.strokeStyle=ac.ok?'rgba(255,255,255,0.30)':'rgba(255,107,107,0.55)';ctx.lineWidth=1;
          ctx.strokeRect(lx+0.5,ly+0.5,lw-1,lh-1);
          ctx.fillStyle=ac.ok?'#eaeaea':'#ff8b8b';
          ctx.fillText(p.label,lx+7,ly+11);ctx.textAlign='center';
        }});
        // Houses
        (HOUSES[zone]||[]).forEach(h=>{if(h.dx>=camX&&h.dx<camX+VW&&h.dy>=camY&&h.dy<camY+VH){
          // La maison occupe 3 cases de large sur 3 de haut : on regarde si la place est
          // libre avant de la poser, sinon elle se plaque sur les murs de la piece.
          let libre=true;
          for(let i2=-2;i2<=2&&libre;i2++)for(let j2=-3;j2<=1&&libre;j2++){
            const c2=map[h.dy+j2]?.[h.dx+i2];
            if(c2===undefined||!isWalk(c2))libre=false;
          }
          dSprite(ctx,h.dx-camX,h.dy-camY,'house',{placeLibre:libre},tick);
          if(Math.abs(h.dx-pos.x)<=1&&Math.abs(h.dy-pos.y)<=1&&!anyOv){
            ctx.fillStyle='#ffd700';ctx.font='bold 9px monospace';ctx.textAlign='center';
            ctx.fillText('🚪[E]',(h.dx-camX)*T+T/2,(h.dy-camY)*T+T+9);}
        }});
        // Chests
        chests.forEach(c=>{if(!chestsOpened[c.id]&&c.x>=camX&&c.x<camX+VW&&c.y>=camY&&c.y<camY+VH)dSprite(ctx,c.x-camX,c.y-camY,'chest',{},tick);});
        // Events
        events.filter(e=>e.zid===zone).forEach(e=>{if(e.x>=camX&&e.x<camX+VW&&e.y>=camY&&e.y<camY+VH){dSprite(ctx,e.x-camX,e.y-camY,'enemy',e,tick);}});
        // Enemies
        enemies.filter(e=>e.zid===zone).forEach(e=>{if(e.x>=camX&&e.x<camX+VW&&e.y>=camY&&e.y<camY+VH)dSprite(ctx,e.x-camX,e.y-camY,'enemy',e,tick);});
        // NPCs
        npcs.forEach(n=>{if(n.x>=camX&&n.x<camX+VW&&n.y>=camY&&n.y<camY+VH){
          const fait=questsDone[n.name]||(n.bossId&&bossWins[n.bossId]);
          const acces=n.bossId?canAccess(zone):{ok:true};
          const marqueur=fait?{signe:'\u2713',couleur:'#5ac47a'}
            :n.boss?(acces.ok?{signe:'\ud83d\udc80',couleur:'#ff5c5c'}:{signe:'\ud83d\udd12',couleur:'#8792a6'})
            :n.mission?{signe:'!',couleur:'#ff8c42'}
            :n.pool?{signe:'?',couleur:'#ffd479'}:null;
          dSprite(ctx,n.x-camX,n.y-camY,'npc',{...n,marqueur},tick);
          if(!fait&&Math.abs(n.x-pos.x)<=1&&Math.abs(n.y-pos.y)<=1&&!anyOv){
            ctx.fillStyle='#fff';ctx.font='bold 10px monospace';ctx.textAlign='center';
            ctx.fillText('[E]',(n.x-camX)*T+T/2,(n.y-camY)*T+T+9);}
        }});
        // ⭐ La cible de l'objectif : anneau au sol si elle est visible, fleche au bord
        // de l'ecran sinon. Le joueur ne doit jamais chercher ou aller (R10).
        if(objectif.cible){
          const cx=(objectif.cible.x-camX)*T+T/2,cy=(objectif.cible.y-camY)*T+T/2;
          const p2=Math.sin(tick*0.07)*0.35+0.65;
          if(cx>0&&cx<CW&&cy>0&&cy<CH){
            ctx.strokeStyle=`rgba(255,212,121,${p2})`;ctx.lineWidth=2;
            ctx.beginPath();ctx.ellipse(cx,cy+T/2-3,T*0.46,T*0.20,0,0,Math.PI*2);ctx.stroke();
          }else{
            const ax=Math.min(Math.max(cx,22),CW-22),ay=Math.min(Math.max(cy,34),CH-22);
            const an=Math.atan2(cy-CH/2,cx-CW/2);
            ctx.save();ctx.translate(ax,ay);ctx.rotate(an);
            ctx.fillStyle=`rgba(255,212,121,${p2})`;
            ctx.beginPath();ctx.moveTo(11,0);ctx.lineTo(-7,-8);ctx.lineTo(-7,8);ctx.closePath();ctx.fill();
            ctx.restore();
          }
        }

        // Companion
        const cp=compP.current;if(cp.x>=camX&&cp.x<camX+VW&&cp.y>=camY&&cp.y<camY+VH)dSprite(ctx,cp.x-camX,cp.y-camY,'companion',{},tick);
        // Player
        dSprite(ctx,pos.x-camX,pos.y-camY,'player',{dir,walk},tick);
        // HUD
        const zd=ZONE_REQ[zone]?MAPS[zone]:null;const zcol={hub:'#f0c040',pods:'#4ecdc4',workloads:'#ff6b6b',network:'#54a0ff',sanctum:'#a55eea'}[zone]||'#fff';
        ctx.fillStyle='rgba(0,0,0,0.7)';ctx.fillRect(0,0,CW,HUD_H);
        ctx.fillStyle=zcol;ctx.font='bold 12px monospace';ctx.textAlign='left';
        const zNpcs=(NPCS[zone]||[]).filter(n=>!n.boss&&(n.pool||n.mission));const zDone=zNpcs.filter(n=>questsDone[n.name]).length;
        ctx.fillText(`${zone==='hub'?'🏠':zone==='pods'?'🏘️':zone==='workloads'?'🏰':zone==='network'?'🌐':'🔮'} ${zone.charAt(0).toUpperCase()+zone.slice(1)} ${zNpcs.length?`(${zDone}/${zNpcs.length})`:''}`,6,16);
        const lv=getLv(xp);ctx.font='12px serif';ctx.textAlign='center';ctx.fillText('❤️'.repeat(hearts)+'🖤'.repeat(3-hearts),CW/2-50,16);
        ctx.font='bold 11px monospace';ctx.fillStyle='#f0c040';ctx.fillText(`Lv${lv.l} ⭐${xp}`,CW/2+50,16);
        ctx.fillStyle=nA>0.3?'#aaa':'#ffd700';ctx.textAlign='right';ctx.fillText(`${nA>0.3?'🌙':'☀️'} ${clock(gt)}`,CW-6,16);
        const nxt=getNxt(xp);const barW=nxt?(xp-lv.x)/(nxt.x-lv.x):1;ctx.fillStyle='#1a1f2e';ctx.fillRect(0,24,CW,4);ctx.fillStyle=nxt?'#f0c040':'#ff9f43';ctx.fillRect(0,24,CW*barW,4);
        // Minimap
        // ⚠️ Elle faisait 60x38 px pour 40x24 cases, avec un mur (#1a1a2a) presque de la
        // meme couleur que le sol (#2a3a2a) : on n'y lisait rien. Plus grande, contrastee,
        // et elle montre desormais ou regarde la camera — sans quoi elle ne situe rien.
        const mmW=96,mmH=Math.round(96*mapH/mapW),mmX=CW-mmW-6,mmY=32;
        ctx.fillStyle='rgba(6,8,12,0.82)';ctx.fillRect(mmX-2,mmY-2,mmW+4,mmH+4);
        ctx.strokeStyle='rgba(255,255,255,0.22)';ctx.lineWidth=1;ctx.strokeRect(mmX-1.5,mmY-1.5,mmW+3,mmH+3);
        const sx=mmW/mapW,sy=mmH/mapH;
        for(let my=0;my<mapH;my++)for(let mx=0;mx<mapW;mx++){const cm=map[my]?.[mx];
          ctx.fillStyle=cm==='~'?'#070a10':(isWalk(cm)?'#55617a':'#161b26');
          ctx.fillRect(mmX+mx*sx,mmY+my*sy,Math.ceil(sx),Math.ceil(sy));}
        ctx.strokeStyle='rgba(255,255,255,0.55)';ctx.lineWidth=1;
        ctx.strokeRect(mmX+camX*sx+0.5,mmY+camY*sy+0.5,VW*sx,VH*sy);
        // Un point avec son cerne : sans le cerne, deux points voisins se fondent l'un
        // dans l'autre et on croit n'en voir qu'un.
        const pt=(mx,my,c,t)=>{ctx.fillStyle='rgba(0,0,0,0.7)';ctx.fillRect(mmX+mx*sx-t-1,mmY+my*sy-t-1,t*2+2,t*2+2);
          ctx.fillStyle=c;ctx.fillRect(mmX+mx*sx-t,mmY+my*sy-t,t*2,t*2);};
        (PORTALS[zone]||[]).forEach(p=>pt(p.x,p.y,'#b06bff',2));
        npcs.forEach(n=>pt(n.x,n.y,n.boss?'#ff4444':n.col,n.boss?2:1.5));
        pt(pos.x,pos.y,'#39ff14',2);
      }
      af=requestAnimationFrame(loop);
    };af=requestAnimationFrame(loop);return()=>cancelAnimationFrame(af);
  },[loaded,zone,pos,dir,walk,dialogUI,questUI,bossUI,termUI,overlay,recap,termOpen,showMap,showFlash,xp,hearts,events,enemies,chests,chestsOpened,gt,inHouse,interact,canAccess,questsDone,bossWins,bossArena]);

  // Ce que le joueur doit faire maintenant. Jamais vide : s'il n'y a plus rien ici,
  // on l'envoie vers la zone suivante (regle R10).
  const objectif=(()=>{
    const zn=(NPCS[zone]||[]).filter(n=>!n.boss&&(n.pool||n.mission));
    // Le plus proche d'abord : un objectif qui envoie a l'autre bout de la carte
    // alors qu'il y a quelqu'un a deux pas est un objectif qui se fait ignorer.
    const reste=zn.filter(n=>!questsDone[n.name])
      .sort((a,b)=>(Math.abs(a.x-pos.x)+Math.abs(a.y-pos.y))-(Math.abs(b.x-pos.x)+Math.abs(b.y-pos.y)));
    if(reste.length)return{texte:`Parler a ${reste[0].name}${reste.length>1?` — il reste ${reste.length} personnes ici`:''}`,cible:reste[0]};
    const boss=(NPCS[zone]||[]).find(n=>n.boss&&!bossWins[n.bossId]);
    if(boss)return{texte:`Affronter ${boss.name} : tout le monde ici t'a repondu`,cible:boss};
    const portail=(PORTALS[zone]||[]).find(p=>canAccess(p.to).ok);
    const suivante=Object.keys(ZONE_REQ).find(z=>z!==zone&&(NPCS[z]||[]).some(n=>!n.boss&&(n.pool||n.mission)&&!questsDone[n.name]));
    if(suivante)return{texte:'Zone terminee. Rejoindre une autre zone par un portail',cible:portail||null};
    return{texte:'Toutes les zones sont terminees. Bravo.',cible:null};
  })();

  const bold=(t,c)=>t.split(/\*\*(.*?)\*\*/g).map((p,i)=>i%2?<strong key={i} style={{color:c||'#f0c040'}}>{p}</strong>:<span key={i}>{p}</span>);

  // Smart text renderer: detects code/terminal lines vs prose
  const renderText=(text,col)=>{
    const lines=text.split('\n');
    const blocks=[];let cur={type:'prose',lines:[]};
    lines.forEach(line=>{
      const trimmed=line.trimStart();
      const isCode=trimmed.startsWith('$')||trimmed.startsWith('NAME')||trimmed.startsWith('NAMESPACE')||trimmed.startsWith('TYPE')||trimmed.startsWith('REVISION')||/^\s{2,}/.test(line)||/^(apiVersion|kind|metadata|spec|status|containers|volumes|rules|data):/.test(trimmed)||/^\w+[-\w]*\s{2,}/.test(trimmed)||trimmed.startsWith('---')||trimmed.startsWith('error:')||trimmed.startsWith('●')||trimmed.startsWith('Server:')||trimmed.startsWith('Address:')||/^\d+\.\d+\.\d+/.test(trimmed)||trimmed.startsWith('- ')||trimmed.startsWith('volumeMounts:')||trimmed.startsWith('livenessProbe:')||trimmed.startsWith('readinessProbe:')||trimmed.startsWith('httpGet:')||trimmed.startsWith('path:')||trimmed.startsWith('port:');
      if(isCode&&cur.type==='prose'&&cur.lines.length>0){blocks.push(cur);cur={type:'code',lines:[line]};}
      else if(isCode){cur.type='code';cur.lines.push(line);}
      else if(!isCode&&cur.type==='code'&&cur.lines.length>0){blocks.push(cur);cur={type:'prose',lines:[line]};}
      else{cur.lines.push(line);}
    });
    if(cur.lines.length>0)blocks.push(cur);
    return blocks.map((b,i)=>{
      if(b.type==='code')return(<div key={i} style={{background:'#0d1117',border:'1px solid #1e2736',borderRadius:6,padding:'8px 10px',margin:'6px 0',fontFamily:"'Courier New',monospace",fontSize:11,color:'#8bda8b',lineHeight:1.5,overflowX:'auto',whiteSpace:'pre'}}>{b.lines.map((l,j)=>{
        if(l.trimStart().startsWith('$'))return(<div key={j}><span style={{color:'#39ff14'}}>$</span><span style={{color:'#ccc'}}>{l.trimStart().slice(1)}</span></div>);
        return(<div key={j} style={{color:'#aaa'}}>{l}</div>);
      })}</div>);
      return(<div key={i} style={{margin:'4px 0',whiteSpace:'pre-wrap'}}>{bold(b.lines.join('\n'),col)}</div>);
    });
  };
  const mm=(dx,dy)=>{if(inHouse)return;setDir(dy<0?'up':dy>0?'down':dx<0?'left':'right');const nx=pos.x+dx,ny=pos.y+dy;const map=MAPS[zone];if(isWalk(map[ny]?.[nx])&&!(NPCS[zone]||[]).some(n=>n.x===nx&&n.y===ny)){compP.current={x:pos.x,y:pos.y};setPos({x:nx,y:ny});const portal=(PORTALS[zone]||[]).find(p=>p.x===nx&&p.y===ny);if(portal){const ac=canAccess(portal.to);if(ac.ok)setTimeout(()=>changeZone(portal.to,portal.sx,portal.sy),100);else{setPMsg(ac.msg);setTimeout(()=>setPMsg(''),2500);}}}};

  if(!loaded) return (<div style={{background:'#080c12',color:'#f0c040',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'monospace',fontSize:16}}>🌐 KubeWorld V6...</div>);

  return (
    <div style={{background:'#080c12',minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',fontFamily:"'Courier New',monospace",userSelect:'none'}}>
      <div style={{position:'relative',width:CW,maxWidth:'100vw',overflow:'hidden',border:'3px solid #2a3445',borderRadius:8,marginTop:4,boxShadow:'0 0 40px rgba(0,0,0,0.9)'}}>
        <canvas ref={cvs} style={{display:'block',width:'100%',aspectRatio:`${VW}/${VH}`,imageRendering:'pixelated',transform:shakeRef.current>0?`translate(${(Math.random()-0.5)*shakeRef.current*0.8}px,${(Math.random()-0.5)*shakeRef.current*0.8}px)`:''}} tabIndex={0}/>

        {/* DIALOG */}
        {dialogUI&&<div style={S.dlg} onClick={advDlg}>
          <div style={{display:'flex',justifyContent:'space-between'}}><span style={{fontSize:13,fontWeight:700,color:dialogUI.npc.col}}>{dialogUI.npc.icon} {dialogUI.npc.name}</span><span style={{fontSize:9,color:'#555'}}>E ou clic →</span></div>
          <div style={{fontSize:12,color:'#ddd',lineHeight:1.7,marginTop:6,whiteSpace:'pre-wrap'}}>{dialogUI.npc.lines.map((l,i)=><div key={i} style={{marginTop:i?5:0}}>{renderText(l,dialogUI.npc.col)}</div>)}</div>
        </div>}

        {/* QUEST (QCM) */}
        {questUI&&<div style={S.ov}><div style={S.qb}>
          <div style={{display:'flex',justifyContent:'space-between',borderBottom:`2px solid ${questUI.npc.col}`,paddingBottom:8,marginBottom:10}}>
            <span style={{fontSize:14,fontWeight:700,color:questUI.npc.col}}>{questUI.npc.icon} {questUI.rev?(questUI.fresh?'Révision · nouvelle question':'Révision'):questUI.q.les?'Cours':'Quiz'}</span>
            <button onClick={()=>setQst(null)} style={S.xb}>✕ Échap</button>
          </div>

          {/* LESSON PHASE */}
          {questUI.lesIdx!=null&&questUI.q.les&&questUI.lesIdx<questUI.q.les.length&&(
            <div>
              <div style={{fontSize:10,color:'#666',marginBottom:8}}>Leçon {questUI.lesIdx+1}/{questUI.q.les.length} — puis Quiz</div>
              <div style={{fontSize:12,color:'#ddd',lineHeight:1.7,maxHeight:280,overflowY:'auto'}}>
                {renderText(questUI.q.les[questUI.lesIdx],questUI.npc.col)}
              </div>
              <button onClick={()=>setQst(u=>{
                if(u.lesIdx<u.q.les.length-1)return{...u,lesIdx:u.lesIdx+1};
                return{...u,lesIdx:null};
              })} style={{...S.gb,width:'100%',marginTop:10}}>
                {questUI.lesIdx<questUI.q.les.length-1?'Suivant →':'Passer au Quiz 📝'}
              </button>
            </div>
          )}

          {/* QCM PHASE */}
          {(questUI.lesIdx==null||!questUI.q.les)&&(<div>
            <div style={{fontSize:13,color:'#eee',lineHeight:1.6,marginBottom:12}}>{questUI.q.q}</div>
            {questUI.q.opts.map((o,i)=>{const ans=questUI.answer;const isC=i===questUI.q.c;const ch=ans===i;
              let bg='#151a22',brd='#2a3445',cl='#ccc';
              if(ans!=null){if(isC){bg='#0d2a0d';brd='#39ff14';cl='#39ff14';}else if(ch){bg='#2a0d0d';brd='#ff4444';cl='#ff6b6b';}else cl='#555';}
              return (<button key={i} onClick={()=>{if(ans!=null)return;setQst(u=>({...u,answer:i}));}} style={{display:'block',width:'100%',background:bg,border:`1px solid ${brd}`,borderRadius:8,padding:'10px 14px',textAlign:'left',cursor:ans!=null?'default':'pointer',fontFamily:'inherit',fontSize:12,color:cl,marginBottom:6}}>
                <span style={{fontWeight:700,marginRight:8}}>{ans!=null?(isC?'✓':ch?'✗':'·'):String.fromCharCode(65+i)+'.'}</span>{o}</button>);})}
            {questUI.answer!=null&&<><div style={{marginTop:8,padding:10,borderRadius:8,background:questUI.answer===questUI.q.c?'#0a1a0a':'#1a0a0a',fontSize:12,color:'#ccc',lineHeight:1.6}}>{renderText(questUI.q.exp,questUI.npc.col)}</div>
              <button onClick={()=>{if(questUI.answer===questUI.q.c)completeQuest(questUI.npc,questUI.q,questUI);setQst(null);}} style={{...S.gb,width:'100%',marginTop:8}}>{questUI.answer===questUI.q.c?(questUI.rev?'📖 Compris':'✅ Terminé !'):'Fermer'}</button></>}
          </div>)}
        </div></div>}

        {/* BOSS */}
        {bossUI&&<div style={{...S.ov,background:'rgba(0,0,0,0.55)'}}><div style={{...S.qb,borderColor:'#ff4444'}}>
          <div style={{display:'flex',justifyContent:'space-between',borderBottom:'2px solid #ff4444',paddingBottom:8,marginBottom:10}}>
            <span style={{color:'#ff4444',fontWeight:700}}>💀 {bossUI.b.name} {bossUI.phase+1}/{bossUI.b.phases.length}</span>
            <span style={{color:'#666',fontSize:11}}>{bossUI.fails}/3</span>
          </div>
          <div style={{fontSize:13,color:'#eee',lineHeight:1.6,marginBottom:10}}>{bossUI.b.phases[bossUI.phase].q}</div>
          {bossUI.result==='ok'&&<div style={S.ok}>{bossUI.b.phases[bossUI.phase].ok}</div>}
          {bossUI.result!=='ok'&&<><div style={{fontSize:11,color:'#f0c040',background:'#1a1800',padding:'6px 10px',borderRadius:6,marginBottom:8}}>💡 {bossUI.b.phases[bossUI.phase].h[Math.min(bossUI.fails,bossUI.b.phases[bossUI.phase].h.length-1)]}</div>
            <div style={S.pr}><span style={{color:'#ff4444',marginRight:6}}>$</span>
              <input ref={iRef} value={bossUI.input} onChange={e=>setBoss(u=>({...u,input:e.target.value}))}
                onKeyDown={e=>{if(e.key==='Enter'){const inp=bossUI.input.trim().toLowerCase();const ph=bossUI.b.phases[bossUI.phase];
                  if(ph.a.some(a=>inp.includes(a.toLowerCase())))setBoss(u=>({...u,result:'ok'}));
                  else{playSound('wrong');triggerShake(8);const nf=bossUI.fails+1;if(nf>=3){setBoss(u=>({...u,result:'ok'}));nfy("💡 Révélé");}else setBoss(u=>({...u,fails:nf,input:''}));}}}}
                style={{...S.inp,color:'#ff6b6b'}} placeholder="Commande..." autoFocus/></div></>}
          <div style={{display:'flex',gap:6,marginTop:10}}>
            {bossUI.result==='ok'&&<button onClick={()=>{
              if(bossUI.phase<bossUI.b.phases.length-1){setBoss(u=>({...u,phase:u.phase+1,input:'',result:null,fails:0}));setTimeout(()=>iRef.current?.focus(),100);}
              else{playSound('boss');setBossArena(null);setBW(w=>{const next={...w,[bossUI.id]:true};if(Object.keys(next).length>=4){checkAch('boss_slayer');setTimeout(()=>setShowVictory(true),500);}return next;});addXP(bossUI.b.xp);setHearts(3);setBoss(null);nfy(`🏆 ${bossUI.b.name} vaincu !`);}
            }} style={{...S.gb,flex:1,background:'#ff4444'}}>{bossUI.phase<bossUI.b.phases.length-1?'Suite →':'🏆 Victoire !'}</button>}
            <button onClick={()=>{playSound('hit');triggerShake();setHearts(h=>Math.max(0,h-1));nfy("💔 -1❤️");setBoss(null);setBossArena(null);}} style={S.xb}>Fuir</button>
          </div>
        </div></div>}

        {/* MISSION TERMINAL - panneau, pas fenetre modale : le terminal du jeu reste utilisable */}
        {misUI&&<div data-mission={misUI.result||'encours'} style={{position:'absolute',top:32,left:10,right:10,background:'rgba(8,12,18,0.97)',border:'2px solid #54a0ff',borderRadius:10,padding:'10px 12px',zIndex:12,maxHeight:'78%',overflowY:'auto'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid #1e2736',paddingBottom:6,marginBottom:8}}>
            <span style={{color:'#54a0ff',fontWeight:700,fontSize:13}}>{misUI.m.icon} {misUI.m.name}</span>
            <span style={{color:'#666',fontSize:10}}>{misUI.result==='fin'?'clôturé':`étape ${misUI.phase+1}/${misUI.m.phases.length}`}</span>
          </div>
          {misUI.phase===0&&misUI.result!=='fin'&&<div style={{fontSize:10,color:'#8792a6',lineHeight:1.6,marginBottom:8,borderLeft:'2px solid #1e2736',paddingLeft:8}}>{renderText(misUI.m.contexte,'#54a0ff')}</div>}
          {misUI.result==='fin'
            ?<div style={{fontSize:12,color:'#ddd',lineHeight:1.7}}>{renderText(misUI.m.fin,'#54a0ff')}</div>
            :<><div style={{fontSize:12,color:'#eee',lineHeight:1.6,marginBottom:8}}>{renderText(misUI.m.phases[misUI.phase].q,'#54a0ff')}</div>
              {misUI.result==='ok'
                ?<div style={{fontSize:11,color:'#39ff14',background:'#0d2a0d',border:'1px solid #39ff1444',borderRadius:6,padding:'6px 10px'}}>✅ Étape validée - la sortie est dans le terminal.</div>
                :<div style={{fontSize:10,color:'#f0c040',background:'#1a1800',padding:'6px 10px',borderRadius:6}}>⌨️ Tape la commande dans le terminal, en bas. {misUI.fails>0&&`(${misUI.fails}/3 essais)`}</div>}</>}
          <div style={{display:'flex',gap:6,marginTop:10}}>
            {misUI.result==='ok'&&<button onClick={()=>{
              if(misUI.phase<misUI.m.phases.length-1){
                const suivante=misUI.phase+1;
                setMis(u=>({...u,phase:suivante,result:null,fails:0}));
                setTermHist(h=>[...h,{type:'sys',text:`${misUI.m.icon} étape ${suivante+1}/${misUI.m.phases.length} : ${misUI.m.phases[suivante].q.replace(/\*\*/g,'')}`}].slice(-100));
                setTimeout(()=>termRef2.current?.focus(),100);
              }else setMis(u=>({...u,result:'fin'}));
            }} style={{...S.gb,flex:1,background:'#54a0ff',fontSize:11,padding:'8px 12px'}}>{misUI.phase<misUI.m.phases.length-1?'Étape suivante →':'Clôturer l\'incident'}</button>}
            {misUI.result==='fin'&&<button onClick={()=>{
              const{npc,m,rev}=misUI;setMis(null);
              if(rev){nfy('\ud83d\udcdf Incident rejoué');return;}
              playSound('boss');
              setQD(d=>{const next={...d,[npc.name]:true};
                const tous=Object.values(NPCS).flat().filter(n=>!n.boss&&(n.pool||n.mission));
                if(tous.every(n=>next[n.name]))checkAch('completionist');
                return next;});
              addXP(m.xp);
              setTermCount(c=>{const n=c+1;if(n>=20)checkAch('terminal_warrior');return n;});
              if(npc.gives&&!inv.includes(npc.gives)){setInv(i=>{const next=[...i,npc.gives];if(next.length>=12)checkAch('kubectl_master');return next;});}
              setJnl(j=>j.some(e=>e.t===m.name)?j:[...j,{t:m.name,c:m.fin,i:m.icon}]);
              nfy(`✅ Incident résolu : +${m.xp} XP`);
            }} style={{...S.gb,flex:1,fontSize:11,padding:'8px 12px'}}>Terminer</button>}
            <button onClick={()=>setMis(null)} style={{...S.xb,fontSize:10}}>{misUI.result==='fin'?'Fermer':'Abandonner'}</button>
          </div>
        </div>}

        {/* TERMINAL */}
        {termUI&&<div style={S.ov}><div style={S.qb}>
          <div style={{display:'flex',justifyContent:'space-between',borderBottom:'2px solid #ff8c00',paddingBottom:6,marginBottom:10}}>
            <span style={{color:'#ff8c00',fontWeight:700}}>{termUI.ev.icon} {termUI.ev.name||termUI.ev.t||'Incident'}</span>
            <button onClick={()=>{if(termUI.result==='ok'&&!termUI.isEnemy)setEvents(ev=>ev.filter(x=>!(x.x===termUI.ev.x&&x.y===termUI.ev.y)));setTerm(null);}} style={S.xb}>✕</button>
          </div>
          <div style={{fontSize:12,color:'#ccc',lineHeight:1.5,marginBottom:8}}>{termUI.ev.d}</div>
          {termUI.result!=='ok'&&<><div style={{fontSize:11,color:'#f0c040',background:'#1a1800',padding:'6px 10px',borderRadius:6,marginBottom:8}}>💡 {termUI.ev.h[Math.min(termUI.fails,termUI.ev.h.length-1)]}</div>
            <div style={S.pr}><span style={{color:'#39ff14',marginRight:6}}>$</span>
              <input ref={iRef} value={termUI.input} onChange={e=>setTerm(u=>({...u,input:e.target.value}))}
                onKeyDown={e=>{if(e.key==='Enter'){if(termUI.ev.a.some(a=>termUI.input.trim().toLowerCase().includes(a.toLowerCase()))){setTerm(u=>({...u,result:'ok'}));addXP(termUI.ev.xp||20);if(termUI.isEnemy){setEnemyCount(c=>{const n=c+1;if(n>=10)checkAch('bug_hunter');return n;});}else{setTermCount(c=>{const n=c+1;if(n>=20)checkAch('terminal_warrior');return n;});}}else setTerm(u=>({...u,fails:u.fails+1,input:''}));}}}
                style={S.inp} placeholder="kubectl ..." autoFocus/></div></>}
          {termUI.result==='ok'&&<div style={S.ok}>✅ Résolu !</div>}
          {termUI.result==='ok'&&<button onClick={()=>{if(!termUI.isEnemy)setEvents(ev=>ev.filter(x=>!(x.x===termUI.ev.x&&x.y===termUI.ev.y)));setTerm(null);}} style={{...S.gb,width:'100%',marginTop:8}}>OK</button>}
        </div></div>}

        {/* OVERLAYS */}
        {overlay==='inv'&&<div style={S.ov} onClick={()=>setOv(null)}><div style={S.qb} onClick={e=>e.stopPropagation()}>
          <h3 style={{color:'#f0c040',margin:'0 0 8px'}}>📦 {getLv(xp).t} Lv{getLv(xp).l}</h3>
          <div style={{fontSize:11,color:'#888'}}>{'❤️'.repeat(hearts)+'🖤'.repeat(3-hearts)} · {xp} XP · Boss {Object.keys(bossWins).length}/{Object.keys(BOSSES).length}</div>
          <div style={S.bar}><div style={{...S.barF,width:`${getNxt(xp)?((xp-getLv(xp).x)/(getNxt(xp).x-getLv(xp).x))*100:100}%`}}/></div>
          <div style={{fontSize:10,color:'#555',margin:'8px 0 4px'}}>COMMANDES ({inv.length})</div>
          <div style={{maxHeight:140,overflowY:'auto'}}>{inv.map((c,i)=>(<div key={i} style={S.cmd}><span style={{color:'#39ff14'}}>$</span> {c}</div>))}</div>
          <button onClick={()=>setOv(null)} style={{...S.gb,width:'100%',marginTop:10}}>Fermer</button>
        </div></div>}
        {overlay==='jnl'&&<div style={S.ov} onClick={()=>setOv(null)}><div style={S.qb} onClick={e=>e.stopPropagation()}>
          <h3 style={{color:'#54a0ff',margin:'0 0 10px'}}>📖 Journal ({journal.length})</h3>
          <div style={{maxHeight:260,overflowY:'auto'}}>{journal.length===0?<div style={{color:'#666'}}>Vide</div>:journal.map((e,i)=>(<details key={i} style={{marginBottom:4}}><summary style={{fontSize:12,color:'#ddd',cursor:'pointer'}}>{e.i} {e.t}</summary><div style={{fontSize:11,color:'#aaa',padding:'6px 10px',lineHeight:1.5}}>{renderText(e.c,'#54a0ff')}</div></details>))}</div>
          <button onClick={()=>setOv(null)} style={{...S.gb,width:'100%',marginTop:10}}>Fermer</button>
        </div></div>}
        {overlay==='menu'&&<div style={S.ov} onClick={()=>setOv(null)}><div style={S.qb} onClick={e=>e.stopPropagation()}>
          <h3 style={{color:'#f0c040',margin:'0 0 10px'}}>⚙️ Menu</h3>
          <div style={{fontSize:11,color:'#aaa',lineHeight:2.2,marginBottom:12}}>
            <div><b style={{color:'#54a0ff'}}>Flèches/ZQSD</b> Bouger · <b style={{color:'#54a0ff'}}>E</b> Interagir</div>
            <div><b style={{color:'#54a0ff'}}>I</b> Inventaire · <b style={{color:'#54a0ff'}}>J</b> Journal · <b style={{color:'#54a0ff'}}>P</b> Succes · <b style={{color:'#54a0ff'}}>M</b> Menu · <b style={{color:'#54a0ff'}}>Échap</b> Fermer</div>
          </div>
          <button onClick={async()=>{setZone('hub');setPos({x:13,y:9});setXp(0);setHearts(3);setInv(['kubectl get pods']);setQD({});setBW({});setJnl([]);setCO({});setEvents([]);setEnemies([]);setChests([]);setOv(null);try{localStorage.removeItem('kw6');}catch{}}} style={{...S.xb,width:'100%',background:'#331111',color:'#ff6b6b',marginBottom:6}}>Reset</button>
          <button onClick={()=>setOv(null)} style={{...S.gb,width:'100%'}}>Retour</button>
        </div></div>}

        {notif&&<div style={{position:'absolute',top:30,left:'50%',transform:'translateX(-50%)',background:'rgba(240,192,64,0.95)',color:'#080c12',padding:'4px 14px',borderRadius:12,fontSize:12,fontWeight:700,animation:'fo 3s forwards',pointerEvents:'none'}}>{notif}</div>}
        {portalMsg&&<div style={{position:'absolute',top:'40%',left:'50%',transform:'translate(-50%,-50%)',background:'rgba(30,10,10,0.95)',border:'1px solid #ff444466',borderRadius:10,padding:'12px 20px',fontSize:13,color:'#ff6b6b',textAlign:'center',pointerEvents:'none'}}>{portalMsg}</div>}

        {/* WORLD MAP */}
        {showMap&&<div style={S.ov} onClick={()=>setShowMap(false)}><div style={{...S.qb,maxWidth:520}} onClick={e=>e.stopPropagation()}>
          <h3 style={{color:'#f0c040',margin:'0 0 12px',textAlign:'center'}}>🗺️ Carte du Cluster</h3>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            {[
              {id:'hub',icon:'🏠',name:'Cluster Hub',col:'#f0c040',status:'Toujours accessible'},
              {id:'pods',icon:'🏘️',name:'Pod Village',col:'#4ecdc4',req:'Level 1',boss:'CrashLord'},
              {id:'workloads',icon:'🏰',name:'Workload Fortress',col:'#ff6b6b',req:'Level 3 + Boss Pods',boss:'Rollout Reaper'},
              {id:'network',icon:'🌐',name:'Network Realm',col:'#54a0ff',req:'Level 5 + Boss Workloads',boss:'404 Phantom'},
              {id:'sanctum',icon:'🔮',name:'Arcane Sanctum',col:'#a55eea',req:'Level 7 + Boss Network',boss:'etcd Corruptor'},
            ].map(z=>{const here=zone===z.id;const done=z.boss?bossWins[BOSS_NAME_MAP[z.boss]]:false;const unlocked=canAccess(z.id).ok;
              return (<div key={z.id} style={{background:here?'#1a2a1a':'#0d1117',border:`2px solid ${here?'#39ff14':done?z.col+'66':'#1e2736'}`,borderRadius:8,padding:10,opacity:unlocked?1:0.5}}>
                <div style={{fontSize:16,marginBottom:4}}>{z.icon} <span style={{color:z.col,fontSize:13,fontWeight:700}}>{z.name}</span></div>
                {here&&<div style={{fontSize:9,color:'#39ff14'}}>📍 Tu es ici</div>}
                {done&&<div style={{fontSize:9,color:'#39ff14'}}>✅ Boss vaincu</div>}
                {!done&&z.boss&&unlocked&&<div style={{fontSize:9,color:'#ff6b6b'}}>💀 {z.boss}</div>}
                {!unlocked&&<div style={{fontSize:9,color:'#666'}}>🔒 {z.req}</div>}
                <div style={{fontSize:9,color:'#555',marginTop:4}}>PNJ: {(NPCS[z.id]||[]).filter(n=>!n.boss&&questsDone[n.name]).length}/{(NPCS[z.id]||[]).filter(n=>!n.boss).length}</div>
              </div>);
            })}
          </div>
          <div style={{textAlign:'center',marginTop:10,fontSize:10,color:'#666'}}>Level {getLv(xp).l} — {xp} XP — Boss {Object.keys(bossWins).length}/4</div>
          <button onClick={()=>setShowMap(false)} style={{...S.gb,width:'100%',marginTop:10}}>Fermer (Tab)</button>
        </div></div>}

        {/* FLASHCARD */}
        {showFlash&&flashCard&&<div style={S.ov} onClick={()=>setShowFlash(false)}><div style={{...S.qb,maxWidth:420,textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <h3 style={{color:'#feca57',margin:'0 0 12px'}}>🃏 Flashcard</h3>
          <div style={{background:'#151a22',border:'1px solid #2a3445',borderRadius:10,padding:20,minHeight:80,cursor:'pointer',marginBottom:10}} onClick={()=>setFlashCard(c=>{if(c?.flipped){setFlashCount(fc=>{const n=fc+1;if(n>=30)checkAch('flash_addict');return n;});return genFlashcard();}return{...c,flipped:true};})}>
            {!flashCard.flipped?
              <div style={{fontSize:14,color:'#eee',lineHeight:1.6}}>{flashCard.front}</div>
              :<div><div style={{fontSize:11,color:'#888',marginBottom:8,textDecoration:'line-through'}}>{flashCard.front}</div><div style={{fontSize:13,color:'#39ff14',lineHeight:1.6}}>{flashCard.back}</div></div>
            }
          </div>
          <div style={{fontSize:10,color:'#666',marginBottom:10}}>{flashCard.flipped?'Clic pour nouvelle carte':'Clic pour révéler la réponse'}</div>
          <button onClick={()=>setShowFlash(false)} style={{...S.gb,width:'100%'}}>Fermer (F)</button>
        </div></div>}

        {/* ACHIEVEMENTS */}
        {overlay==='ach'&&<div style={S.ov} onClick={()=>setOv(null)}><div style={S.qb} onClick={e=>e.stopPropagation()}>
          <h3 style={{color:'#feca57',margin:'0 0 12px'}}>🏆 Achievements ({Object.keys(achievements).length}/{Object.keys(ACHS).length})</h3>
          <div style={{maxHeight:280,overflowY:'auto'}}>{Object.entries(ACHS).map(([k,a])=>(
            <div key={k} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',marginBottom:4,background:achievements[k]?'#0d2a0d':'#151a22',border:`1px solid ${achievements[k]?'#39ff1444':'#1e2736'}`,borderRadius:6}}>
              <span style={{fontSize:18,opacity:achievements[k]?1:0.3}}>{a.icon}</span>
              <div><div style={{fontSize:12,color:achievements[k]?'#39ff14':'#555',fontWeight:700}}>{a.name}</div>
              <div style={{fontSize:10,color:achievements[k]?'#aaa':'#444'}}>{a.desc}</div></div>
              {achievements[k]&&<span style={{marginLeft:'auto',color:'#39ff14',fontSize:10}}>Unlocked</span>}
            </div>))}</div>
          <button onClick={()=>setOv(null)} style={{...S.gb,width:'100%',marginTop:10}}>Fermer (A)</button>
        </div></div>}

        {/* VICTORY SCREEN */}
        {showVictory&&<div style={S.ov} onClick={()=>setShowVictory(false)}><div style={{...S.qb,maxWidth:480,textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:40,marginBottom:8}}>🎉</div>
          <h2 style={{color:'#f0c040',margin:'0 0 8px'}}>Cluster Sauvé !</h2>
          <div style={{fontSize:13,color:'#ddd',lineHeight:1.8,marginBottom:16}}>
            Tu as vaincu les 4 boss et maîtrisé les fondamentaux de Kubernetes !
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:16}}>
            <div style={{background:'#151a22',borderRadius:8,padding:10}}><div style={{color:'#f0c040',fontSize:20,fontWeight:700}}>{getLv(xp).l}</div><div style={{color:'#888',fontSize:10}}>Level</div></div>
            <div style={{background:'#151a22',borderRadius:8,padding:10}}><div style={{color:'#39ff14',fontSize:20,fontWeight:700}}>{xp}</div><div style={{color:'#888',fontSize:10}}>XP Total</div></div>
            <div style={{background:'#151a22',borderRadius:8,padding:10}}><div style={{color:'#54a0ff',fontSize:20,fontWeight:700}}>{Object.keys(questsDone).length}</div><div style={{color:'#888',fontSize:10}}>Quêtes</div></div>
            <div style={{background:'#151a22',borderRadius:8,padding:10}}><div style={{color:'#feca57',fontSize:20,fontWeight:700}}>{inv.length}</div><div style={{color:'#888',fontSize:10}}>Commandes</div></div>
          </div>
          <div style={{fontSize:11,color:'#aaa',marginBottom:12}}>Continue à explorer pour compléter toutes les quêtes et achievements !</div>
          <button onClick={()=>setShowVictory(false)} style={{...S.gb,width:'100%',background:'#f0c040'}}>Continuer l'aventure</button>
        </div></div>}

        {/* ZONE TRANSITION */}
        {zoneTransition&&<div style={{position:'absolute',top:0,left:0,right:0,bottom:0,background:'#080c12',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',zIndex:25,animation:'fadeIn 0.3s'}}>
          <div style={{fontSize:28,color:'#f0c040',fontWeight:700,marginBottom:8,textShadow:'0 0 20px rgba(240,192,64,0.5)'}}>{zoneTransition.text}</div>
          <div style={{fontSize:14,color:'#888',fontStyle:'italic'}}>{zoneTransition.sub}</div>
        </div>}

        {/* BOSS INTRO CUTSCENE */}
        {bossIntro&&<div style={S.ov}><div style={{...S.qb,maxWidth:480,textAlign:'center',borderColor:'#ff4444'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:36,marginBottom:8}}>{bossIntro.boss.icon||'💀'}</div>
          <h2 style={{color:'#ff4444',margin:'0 0 12px',textShadow:'0 0 10px rgba(255,40,40,0.5)'}}>{bossIntro.boss.name}</h2>
          <div style={{fontSize:13,color:'#ddd',lineHeight:1.8,marginBottom:16,minHeight:60}}>
            {renderText(bossIntro.boss.intro[bossIntro.idx],'#ff6b6b')}
          </div>
          <button onClick={()=>{
            if(bossIntro.idx<bossIntro.boss.intro.length-1){setBossIntro(bi=>({...bi,idx:bi.idx+1}));}
            else{const id=bossIntro.id;const boss=bossIntro.boss;setBossIntro(null);setBossArena(id);setBoss({b:boss,id,phase:0,input:'',fails:0,result:null});setTimeout(()=>iRef.current?.focus(),300);}
          }} style={{...S.gb,width:'100%',background:'#ff4444',color:'#fff'}}>
            {bossIntro.idx<bossIntro.boss.intro.length-1?'...':'⚔️ COMBAT !'}
          </button>
        </div></div>}

        {/* ZONE INTRO NARRATIVE */}
        {zoneIntroShow&&<div style={S.ov} onClick={()=>setZoneIntroShow(null)}><div style={{...S.qb,maxWidth:420,textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:24,marginBottom:8}}>{zone==='hub'?'🏠':zone==='pods'?'🏘️':zone==='workloads'?'🏰':zone==='network'?'🌐':'🔮'}</div>
          <h2 style={{color:{hub:'#f0c040',pods:'#4ecdc4',workloads:'#ff6b6b',network:'#54a0ff',sanctum:'#a55eea'}[zone]||'#fff',margin:'0 0 4px'}}>{zoneIntroShow.title}</h2>
          <div style={{fontSize:12,color:'#888',fontStyle:'italic',marginBottom:12}}>{zoneIntroShow.sub}</div>
          <div style={{fontSize:12,color:'#ccc',lineHeight:1.8,whiteSpace:'pre-wrap',marginBottom:16}}>{zoneIntroShow.text}</div>
          <button onClick={()=>setZoneIntroShow(null)} style={{...S.gb,width:'100%'}}>Explorer</button>
        </div></div>}

        {/* STORY INTRO (first launch) */}
        {showIntro&&<div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'#080c12',display:'flex',overflowY:'auto',zIndex:30}}>
          <div style={{maxWidth:460,width:'100%',textAlign:'center',padding:20,margin:'auto',boxSizing:'border-box'}}>
            {introIdx===0&&<><div style={{fontSize:48,marginBottom:16}}>🌐</div><h1 style={{color:'#f0c040',margin:'0 0 8px',fontSize:28}}>KubeWorld</h1><div style={{color:'#888',fontSize:13,marginBottom:20}}>Un RPG pour maîtriser Kubernetes</div><div style={{color:'#ccc',fontSize:13,lineHeight:2,marginBottom:20}}>Le cluster est en {bold('**danger**')}.<br/>Un chaos corrupteur s'est infiltré dans chaque couche de l'infrastructure.<br/>Les {bold('**4 Gardiens corrompus**')} contrôlent les zones.</div></>}
            {introIdx===1&&<><div style={{fontSize:36,marginBottom:12}}>⚔️</div><h2 style={{color:'#ff6b6b',margin:'0 0 16px'}}>Ta Mission</h2><div style={{color:'#ccc',fontSize:13,lineHeight:2,marginBottom:20}}>Tu es le nouvel {bold('**SRE**')}.<br/>Maîtrise les commandes {bold('**kubectl**')}, comprends chaque concept,<br/>et {bold('**vaincs les 4 boss**')} pour restaurer l'ordre du cluster.</div></>}
            {introIdx===2&&<><div style={{fontSize:36,marginBottom:12}}>🎮</div><h2 style={{color:'#39ff14',margin:'0 0 16px'}}>Contrôles</h2><div style={{color:'#ccc',fontSize:12,lineHeight:2.4,marginBottom:20}}><b style={{color:'#54a0ff'}}>Flèches / ZQSD</b> — Se déplacer (WASD sur clavier anglais)<br/><b style={{color:'#54a0ff'}}>E</b> — Interagir avec les PNJ et objets<br/><b style={{color:'#54a0ff'}}>T/²</b> — Terminal kubectl (Échap pour fermer)<br/><b style={{color:'#54a0ff'}}>Tab</b> — Carte du monde<br/><b style={{color:'#54a0ff'}}>I/J/P/F</b> — Inventaire / Journal / Succes / Flashcards</div></>}
            <button onClick={()=>{if(introIdx<2)setIntroIdx(i=>i+1);else{setShowIntro(false);localStorage.setItem('kw6_intro_done','1');}}} style={{...S.gb,width:'100%',fontSize:14,padding:12}}>{introIdx<2?'Suivant →':'Commencer l\'aventure'}</button>
            <div style={{display:'flex',justifyContent:'center',gap:8,marginTop:12}}>{[0,1,2].map(i=>(<div key={i} style={{width:8,height:8,borderRadius:4,background:i===introIdx?'#f0c040':'#333'}}/>))}</div>
          </div>
        </div>}

        {/* TERMINAL OVERLAY (full mode) */}
        {termOpen==='full'&&<div style={{position:'absolute',top:0,left:0,right:0,bottom:0,background:'#0d1117',zIndex:20,display:'flex',flexDirection:'column'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 10px',background:'#161b22',borderBottom:'1px solid #1e2736'}}>
            <div style={{display:'flex',gap:6}}>
              {['split','full','float'].map(m=>(<button key={m} onClick={()=>setTermOpen(m)} style={{...S.xb,background:termOpen===m?'#2a3a2a':'#1e2736',color:termOpen===m?'#39ff14':'#888',fontSize:9,padding:'2px 8px'}}>{m}</button>))}
            </div>
            <span style={{color:'#39ff14',fontSize:10}}>KubeWorld Terminal</span>
            <button onClick={()=>setTermOpen(null)} style={{...S.xb,fontSize:9}}>✕ Fermer (Échap)</button>
          </div>
          <div style={{flex:1,overflowY:'auto',padding:8,fontFamily:"'Courier New',monospace",fontSize:11}} ref={el=>{if(el)el.scrollTop=el.scrollHeight;}}>
            {termHist.map((h,i)=>(<div key={i} style={{marginBottom:2,color:h.type==='cmd'?'#ccc':h.type==='err'?'#ff6b6b':h.type==='sys'?'#888':'#8bda8b',whiteSpace:'pre-wrap'}}>{h.type==='cmd'?<><span style={{color:'#39ff14'}}>$</span>{h.text.slice(1)}</>:h.text}</div>))}
          </div>
          <div style={{display:'flex',alignItems:'center',padding:'6px 10px',background:'#161b22',borderTop:'1px solid #1e2736'}}>
            <span style={{color:'#39ff14',marginRight:6,fontFamily:'monospace'}}>$</span>
            <input ref={termRef2} value={termCmd} onChange={e=>setTermCmd(e.target.value)}
              onKeyDown={e=>{
                if(e.key==='Enter'){execCmd(termCmd);setTermCmd('');}
                if(e.key==='ArrowUp'){e.preventDefault();setTermHistIdx(i=>{const ni=Math.min(i+1,termCmdHist.length-1);if(termCmdHist[ni])setTermCmd(termCmdHist[ni]);return ni;});}
                if(e.key==='ArrowDown'){e.preventDefault();setTermHistIdx(i=>{const ni=Math.max(i-1,-1);setTermCmd(ni<0?'':termCmdHist[ni]||'');return ni;});}
              }}
              style={{flex:1,background:'none',border:'none',color:'#ccc',fontFamily:"'Courier New',monospace",fontSize:12,outline:'none'}} placeholder="kubectl ..." autoFocus/>
          </div>
        </div>}
      </div>

      <div style={{width:CW,maxWidth:'100vw',padding:'0 10px',boxSizing:'border-box'}}>
        <div style={{fontSize:11,color:'#ffd479',background:'#141a24',border:'1px solid #2c3a52',borderTop:'none',borderRadius:'0 0 6px 6px',padding:'6px 9px'}}>➤ {objectif.texte}</div>
      </div>

      {/* SPLIT TERMINAL */}
      {termOpen==='split'&&<div style={{width:CW,maxWidth:'100vw',height:180,background:'#0d1117',border:'1px solid #1e2736',borderTop:'none',display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 8px',background:'#161b22',borderBottom:'1px solid #1e2736'}}>
          <div style={{display:'flex',gap:4}}>{['split','full','float'].map(m=>(<button key={m} onClick={()=>{setTermOpen(m);setTimeout(()=>termRef2.current?.focus(),100);}} style={{...S.xb,background:termOpen===m?'#2a3a2a':'transparent',color:termOpen===m?'#39ff14':'#666',fontSize:8,padding:'1px 6px',border:'none'}}>{m}</button>))}</div>
          <span style={{color:'#39ff14',fontSize:9}}>Terminal</span>
          <button onClick={()=>setTermOpen(null)} style={{...S.xb,fontSize:8,padding:'1px 6px',border:'none'}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'4px 8px',fontFamily:"'Courier New',monospace",fontSize:10}} ref={el=>{if(el)el.scrollTop=el.scrollHeight;}}>
          {termHist.map((h,i)=>(<div key={i} style={{marginBottom:1,color:h.type==='cmd'?'#ccc':h.type==='err'?'#ff6b6b':h.type==='sys'?'#666':'#8bda8b',whiteSpace:'pre-wrap'}}>{h.type==='cmd'?<><span style={{color:'#39ff14'}}>$</span>{h.text.slice(1)}</>:h.text}</div>))}
        </div>
        <div style={{display:'flex',alignItems:'center',padding:'4px 8px',background:'#161b22',borderTop:'1px solid #1e2736'}}>
          <span style={{color:'#39ff14',marginRight:4,fontFamily:'monospace',fontSize:11}}>$</span>
          <input ref={termRef2} value={termCmd} onChange={e=>setTermCmd(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter'){execCmd(termCmd);setTermCmd('');}
              if(e.key==='ArrowUp'){e.preventDefault();setTermHistIdx(i=>{const ni=Math.min(i+1,termCmdHist.length-1);if(termCmdHist[ni])setTermCmd(termCmdHist[ni]);return ni;});}
              if(e.key==='ArrowDown'){e.preventDefault();setTermHistIdx(i=>{const ni=Math.max(i-1,-1);setTermCmd(ni<0?'':termCmdHist[ni]||'');return ni;});}
            }}
            style={{flex:1,background:'none',border:'none',color:'#ccc',fontFamily:"'Courier New',monospace",fontSize:11,outline:'none'}} placeholder="kubectl ..." autoFocus/>
        </div>
      </div>}

      {/* FLOAT TERMINAL */}
      {termOpen==='float'&&<div style={{position:'fixed',bottom:80,right:20,width:420,height:260,background:'#0d1117',border:'2px solid #1e2736',borderRadius:10,display:'flex',flexDirection:'column',zIndex:30,boxShadow:'0 0 30px rgba(0,0,0,0.8)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 8px',background:'#161b22',borderBottom:'1px solid #1e2736',borderRadius:'8px 8px 0 0',cursor:'grab'}}>
          <div style={{display:'flex',gap:4}}>{['split','full','float'].map(m=>(<button key={m} onClick={()=>{setTermOpen(m);setTimeout(()=>termRef2.current?.focus(),100);}} style={{...S.xb,background:termOpen===m?'#2a3a2a':'transparent',color:termOpen===m?'#39ff14':'#666',fontSize:8,padding:'1px 6px',border:'none'}}>{m}</button>))}</div>
          <span style={{color:'#39ff14',fontSize:9}}>Terminal</span>
          <button onClick={()=>setTermOpen(null)} style={{...S.xb,fontSize:8,padding:'1px 6px',border:'none'}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'4px 8px',fontFamily:"'Courier New',monospace",fontSize:10}} ref={el=>{if(el)el.scrollTop=el.scrollHeight;}}>
          {termHist.map((h,i)=>(<div key={i} style={{marginBottom:1,color:h.type==='cmd'?'#ccc':h.type==='err'?'#ff6b6b':h.type==='sys'?'#666':'#8bda8b',whiteSpace:'pre-wrap'}}>{h.type==='cmd'?<><span style={{color:'#39ff14'}}>$</span>{h.text.slice(1)}</>:h.text}</div>))}
        </div>
        <div style={{display:'flex',alignItems:'center',padding:'4px 8px',background:'#161b22',borderTop:'1px solid #1e2736',borderRadius:'0 0 8px 8px'}}>
          <span style={{color:'#39ff14',marginRight:4,fontFamily:'monospace',fontSize:11}}>$</span>
          <input ref={termRef2} value={termCmd} onChange={e=>setTermCmd(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter'){execCmd(termCmd);setTermCmd('');}
              if(e.key==='ArrowUp'){e.preventDefault();setTermHistIdx(i=>{const ni=Math.min(i+1,termCmdHist.length-1);if(termCmdHist[ni])setTermCmd(termCmdHist[ni]);return ni;});}
              if(e.key==='ArrowDown'){e.preventDefault();setTermHistIdx(i=>{const ni=Math.max(i-1,-1);setTermCmd(ni<0?'':termCmdHist[ni]||'');return ni;});}
            }}
            style={{flex:1,background:'none',border:'none',color:'#ccc',fontFamily:"'Courier New',monospace",fontSize:11,outline:'none'}} placeholder="kubectl ..." autoFocus/>
        </div>
      </div>}

      <div style={{width:CW,maxWidth:'100vw',background:'#0f1520',border:'1px solid #2a3445',borderTop:'none',borderRadius:'0 0 8px 8px',padding:'6px 10px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
          <div style={{fontSize:10,color:'#ccc'}}>{getLv(xp).t} {'❤️'.repeat(hearts)+'🖤'.repeat(3-hearts)}</div>
          <div style={{display:'flex',gap:3}}>
            {[['📦','I',()=>setOv('inv')],['📖','J',()=>setOv('jnl')],['🏆','P',()=>setOv('ach')],['🗺️','Tab',()=>setShowMap(true)],['🃏','F',()=>{setFlashCard(genFlashcard());setShowFlash(true);}],['💻','T',()=>{setTermOpen(o=>o?null:'split');setTimeout(()=>termRef2.current?.focus(),100);}],['⚙️','M',()=>setOv('menu')]].map(([ic,k,fn])=>(<button key={k} onClick={fn} style={S.bb}>{ic}<span style={{fontSize:7}}>{k}</span></button>))}
          </div>
        </div>
        <div style={{display:'flex',justifyContent:'center',gap:3}}>
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
            <button onPointerDown={()=>mm(0,-1)} style={S.dp}>▲</button>
            <div style={{display:'flex',gap:3}}>
              <button onPointerDown={()=>mm(-1,0)} style={S.dp}>◄</button>
              <button onPointerDown={interact} style={{...S.dp,background:'#1a3a1a',color:'#39ff14',fontWeight:700}}>E</button>
              <button onPointerDown={()=>mm(1,0)} style={S.dp}>►</button>
            </div>
            <button onPointerDown={()=>mm(0,1)} style={S.dp}>▼</button>
          </div>
        </div>
      </div>
      <style>{`@keyframes fo{0%{opacity:0}10%{opacity:1}75%{opacity:1}100%{opacity:0}}@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
    </div>
  );
}
